/**
 * /calculus-solver — производная, интеграл и предел с решением по правилам.
 *
 * Ответ даёт любой CAS; оценку ставят за РЕШЕНИЕ. Поэтому на экране — шаги с
 * названием правила и его формулой, а ответ проверен независимым численным
 * расчётом до показа, и проверка видна строкой под ответом.
 *
 * ⚠️ Слова приходят только из CFG.i18n (ключи перечислены в блейде), формулы —
 * деревьями, которые печатаются нотацией страны (tg или tan). Английских
 * запасных строк здесь нет намеренно: пропущенный ключ должен быть виден.
 *
 * ⚠️ Локализация (01.10): код не клеит к фразам значения и знаки — подстановка
 * только шаблоном `tf`/`tp` (i18n.js), «подпись: формула» — через `label_sep`
 * (fr « : »), числа — `fmt`/`fmtTex` (десятичный знак страны), перечни,
 * промежутки, точки и сторона предела — notation.js. Записи внутри фраз —
 * {ltr: …} (в арабском абзаце <bdi dir="ltr">), формулы — {tex, txt}.
 */
import * as A from './ast.js';
import { toLatex, toText } from './print.js';
import { canon, simplify, mulC, powC } from './simplify.js';
import { notationFor, notationTag, wideSepTex, localizeSeg, fmtNum, fmtNumTex, listJoin, listJoinTex, intervalText as ivText, intervalTex as ivTex, pointText, pointTex, sideText as sideSuffix, sideTex as sideSuffixTex, localizeSrc } from './notation.js';
import { fill, fillParts, partText } from './i18n.js';
import { readExpr, readSpecial, solveDerivative, studyDerivative, solveIntegral, solveLimit, checkAnswer, CalcParseError, stripNotation } from './solve.js';
import { implicitDerivative, paramDerivative, curvePoints } from './implicit.js';
import { parsePoint, parse } from './parse.js';
import { createRenderer, katexCss } from './render.js';
import { generate, fixPoint } from './practice.js';
import { paint, viewMap } from '../plotter/draw.js';
import { diff } from './derive.js';
import { taylor } from './taylor.js';
import { areaBetween } from './area.js';
import { identify } from './numeric.js';
import { SLUG, stage, ae, funnel, complete, diag, caught, critical, settleNow, settleCancel, ensureScript } from './telemetry.js';

const CFG = window.__CAL_CFG || {};
const I18N = CFG.i18n || {};
const PAGE_LOCALE = window.__LOCALE || document.documentElement.lang || 'en';
/* Тег записи: локаль страницы, а у испанской — со страной читателя из языков браузера
   («es-MX» — десятичная точка и (a, b); «es» — запятая и (a; b)). Он идёт во все
   toText / toLatex как `locale`; правило — одно, в notation.js. */
const NAV_LANGS = (navigator.languages && navigator.languages.length) ? Array.from(navigator.languages) : [navigator.language || ''];
const LOCALE = notationTag(PAGE_LOCALE, NAV_LANGS);
const NT = notationFor(LOCALE);
/** Разделитель частей одной строки формулы — знаком перечня страны («;» при десятичной запятой, fr « ; »). */
const WSEP = wideSepTex(NT);
const $ = (id) => document.getElementById(id);
/* «Отрезок [a; b]» в строке перевода пишется записью читателя (es: «[a, b]» в Мексике). */
const t = (k) => (I18N[k] !== undefined ? (typeof I18N[k] === 'string' ? localizeSeg(I18N[k], NT) : I18N[k]) : k);
/** Шаблон перевода → текст / части для DOM (значения {ltr}, {tex, txt} — см. i18n.js). */
const tf = (k, vars) => fill(t(k), vars || {});
const tp = (k, vars) => fillParts(t(k), vars || {});
/** «Подпись» + разделитель перевода: «Касательная: », fr « : ». */
const lab = (k) => t(k) + t('label_sep');
/** То же двоеточие внутри формулы KaTeX: пробел перед ним (fr « : ») ставится явно. */
const labSepTex = () => (/^\s/.test(t('label_sep')) ? '\\ ' : '') + ':\\ ';
const ltr = (s) => ({ ltr: String(s) });
const fmt = (x) => fmtNum(x, NT, 6);
const fmtTex = (x) => fmtNumTex(x, NT, 6);
/** Подписи осей графика — знаком локали (общий плоттер пишет «0.5»). */
const axisNum = (x) => fmtNum(x, NT, 4);

const state = {
    mode: 'derivative', src: '', order: 1, v: null, point: '', kind: 'indefinite', a: '0', b: '1',
    limPoint: '0', side: 0, g: '0', areaA: '', areaB: '', tayPoint: '0', tayOrder: 5, detail: 'short', dnot: 'prime', tan: '', segA: '', segB: '',
    parsed: null, result: null, text: [], latex: [], fromPractice: false,
    auto: true,          // запись подставил сам тул (пример при открытии, при смене режима): её ответ в телеметрию не идёт
};
/** Человек что-то ввёл или выбрал сам — с этого места результат его. */
const touched = () => { state.auto = false; };

let R = createRenderer({ t, locale: LOCALE, dnot: state.dnot });

/* ── ошибки разбора ──────────────────────────────────────────────────────── */
/**
 * Ошибка записи → части сообщения. Место ошибки — отдельной фразой-шаблоном
 * (`err_at` «(знак :n)»), знак и примеры — записью {ltr}: кавычки, порядок
 * слов и разделитель примеров решают перевод и notation.js.
 */
function errText(e) {
    const code = String((e && (e.code || e.message)) || '');
    const at = e && typeof e.at === 'number' ? [' ', ...tp('err_at', { n: e.at + 1 })] : [];
    const withAt = (k, vars) => [...tp(k, vars), ...at];
    if (code === 'empty') return tp('err_empty');
    if (code.startsWith('unexpected_char:')) return withAt('err_char', { ch: ltr(code.slice(16)) });
    if (code === 'expected_paren') return withAt('err_paren');
    if (code === 'unbalanced_paren') return withAt('err_unbalanced');
    if (code === 'expected_bar') return withAt('err_bar');
    if (code === 'expected_arg') return withAt('err_arg');
    if (code === 'unexpected_end') return tp('err_incomplete');
    if (code.startsWith('unexpected_token:')) return withAt('err_char', { ch: ltr(code.slice(17)) });
    if (code === 'inf_in_expr') return tp('err_inf_in_expr');
    if (code.startsWith('unsupported:')) return tp('err_unsupported', { what: ltr(code.slice(12)) });
    if (code === 'point_has_var') return tp('err_point_var');
    if (code === 'bad_inf' || code === 'point_inf') return tp('err_point', { ex: ltr(listJoin(['2', '−1/2', 'pi/4', '∞'], NT)) });
    if (code === 'bad_bound') return tp('err_bound');
    if (code === 'area_bounds') return tp('ar_bounds');
    if (code === 'equation') return tp('err_equation');
    if (code === 'equation_no_y') return tp('err_equation_no_y', { ex: ltr('x^2 + 1') });
    if (code === 'equation_mode') return tp('err_equation_mode');
    return withAt('err_parse');
}

/* ── режим и поля ────────────────────────────────────────────────────────── */
const PREFIX = { derivative: 'f′', integral: '∫', limit: 'lim', taylor: 'Tₙ' };
function setMode(mode, opts = {}) {
    const prev = state.mode;
    state.mode = mode;
    document.querySelectorAll('[data-cal-mode]').forEach((b) => {
        const on = b.getAttribute('data-cal-mode') === mode;
        b.classList.toggle('on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    document.querySelectorAll('[data-cal-for]').forEach((el) => { el.hidden = el.getAttribute('data-cal-for') !== mode; });
    document.querySelectorAll('[data-cal-sample-mode]').forEach((b) => { b.hidden = b.getAttribute('data-cal-sample-mode') !== mode; });
    const inp = $('calInput');
    inp.placeholder = inp.getAttribute('data-ph-' + mode) || inp.placeholder;
    $('calPrefix').textContent = PREFIX[mode];
    $('calDetail').hidden = mode !== 'derivative';
    if (opts.fill && prev !== mode) {
        const first = document.querySelector('[data-cal-sample-mode="' + mode + '"]');
        if (first) { applySample(first, false); state.auto = true; }
    }
    if (prev !== mode && !opts.silent) funnel('mode', { mode, auto: opts.auto ? 1 : 0 });
}

function setSeg(attr, val) {
    // ⚠️ Только кнопки переключателя: у чипов-примеров предела тоже есть data-cal-side,
    // и без этого фильтра они все становились «нажатыми» (синими).
    document.querySelectorAll('.tc-seg-b[' + attr + ']').forEach((b) => {
        const on = b.getAttribute(attr) === String(val);
        b.classList.toggle('on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
}

function readFields() {
    state.src = $('calInput').value;
    state.order = parseInt($('calOrder').value, 10) || 1;
    state.point = $('calPoint').value.trim();
    state.a = $('calA').value.trim();
    state.b = $('calB').value.trim();
    state.limPoint = $('calLimPoint').value.trim();
    state.tayPoint = $('calTayPoint').value.trim();
    state.g = $('calG').value.trim();
    state.areaA = $('calAreaA').value.trim();
    state.areaB = $('calAreaB').value.trim();
    state.tayOrder = parseInt($('calTayOrder').value, 10) || 5;
    state.tan = $('calTan').value.trim();
    state.segA = $('calSegA').value.trim();
    state.segB = $('calSegB').value.trim();
    const vs = $('calVar').value;
    state.v = vs || null;
}

function markInvalid(ids) {
    for (const id of ['calInput', 'calPoint', 'calA', 'calB', 'calG', 'calAreaA', 'calAreaB', 'calLimPoint', 'calTayPoint', 'calTan', 'calSegA', 'calSegB']) {
        const el = $(id);
        if (!el) continue;
        if (ids.includes(id)) el.setAttribute('aria-invalid', 'true'); else el.removeAttribute('aria-invalid');
    }
}

/* ── запуск решения ──────────────────────────────────────────────────────── */
function refresh(fromInput) {
    readFields();
    const err = $('calError');
    err.hidden = true;
    markInvalid([]);
    // перерисовка (подробность шагов, запись производной, тема) запись не меняет — отложенный ответ остаётся в силе
    if (fromInput || !state.src.trim()) settleCancel();
    if (!state.src.trim()) { showPreview(null); showResult(false); fillVars({ vars: ['x'], v: 'x' }); return; }
    if (fromInput && !state.auto) stage('input', { mode: state.mode });

    // Вставили «∫ x² dx» или «lim x→0 …» — режим и поля следуют за записью.
    const pre = stripNotation(state.src);
    if (pre.hints.mode && pre.hints.mode !== state.mode) setMode(pre.hints.mode, { auto: true });
    if (pre.hints.a !== undefined) { $('calA').value = pre.hints.a; $('calB').value = pre.hints.b; setKind('definite'); }
    if (pre.hints.point !== undefined) { $('calLimPoint').value = pre.hints.point; if (pre.hints.side) setSide(pre.hints.side); }
    readFields();

    // Уравнение с y — неявная функция; «x = …, y = …» — параметрическая.
    let sp = null;
    try { sp = readSpecial(state.src); } catch (e) { failWith(errText(e), ['calInput'], fromInput, e); showPreview(null); return; }
    if (sp) {
        if (state.mode !== 'derivative') { failWith(tp('err_equation_mode'), ['calInput'], fromInput, { code: 'equation_mode' }); showPreview(null); return; }
        refreshSpecial(sp, fromInput);
        return;
    }
    $('calDetail').hidden = state.mode !== 'derivative';

    let q;
    try {
        q = readExpr(state.src, state.v);
    } catch (e) {
        failWith(errText(e), ['calInput'], fromInput, e);
        showPreview(null);
        return;
    }
    fillVars(q);
    state.parsed = q;
    R = createRenderer({ t, locale: LOCALE, dnot: state.dnot, v: q.v });
    showPreview(q);
    showWarnings(q);

    // Панели открываем ДО решения: график рисуется в видимый холст (в скрытом у
    // холста нет размера, и первый ответ оставался без графика), а какие панели
    // не нужны (решения нет у неберущегося интеграла) — решает сам режим.
    showResult(true);
    let res;
    const t0 = now();
    try {
        if (state.mode === 'derivative') res = runDerivative(q);
        else if (state.mode === 'integral') res = runIntegral(q);
        else if (state.mode === 'limit') res = runLimit(q);
        else res = runTaylor(q);
    } catch (e) {
        if (e instanceof CalcParseError) {
            const field = { derivative: 'calPoint', integral: state.kind === 'definite' ? (e.field || 'calA') : state.kind === 'area' ? (e.field || 'calG') : 'calInput', limit: 'calLimPoint', taylor: 'calTayPoint' }[state.mode];
            failWith(errText(e), [field], fromInput, e);
            return;
        }
        // не опечатка человека, а наш сбой: ярлык места — в телеметрию, фраза — человеку
        failWith(tp('err_parse'), ['calInput'], false, e);
        caught(e, 'cal_solve_' + state.mode, t('err_parse'));
        return;
    }
    state.result = res;
    flash($('calAnswer'));
    saveHash();
    report(res, fromInput, { order: state.mode === 'derivative' ? state.order : 0, params: q.params.length, ms: Math.round(now() - t0) });
}

const now = () => ((window.performance && performance.now) ? performance.now() : 0);
/** Подпись результата: режим + запись + всё, что меняет ответ (порядок, пределы, точка, сторона). */
function resultKey() {
    const m = state.mode;
    const extra = m === 'derivative' ? [state.order, state.v, state.point]
        : m === 'integral' ? [state.kind, state.a, state.b, state.g, state.areaA, state.areaB]
            : m === 'limit' ? [state.limPoint, state.side] : [state.tayPoint, state.tayOrder];
    return m + '|' + state.src + '|' + extra.join('|');
}
/**
 * «Человек получил ответ» или «не умею» — когда запись отстоялась. Ответ с пометкой
 * «проверка не сошлась» считается ответом (checked: 0); неберущийся интеграл, ненайденный
 * предел, отказ Тейлора — это «не умею»: туда идёт сама запись (до 60 знаков).
 */
function report(res, fromInput, more) {
    if (!fromInput || state.auto) return;
    const kind = String(res.kind || '');
    if (res.ok) {
        stage('solved', { mode: state.mode });
        complete(resultKey(), Object.assign({
            mode: state.mode, kind, rule: res.rule || '', checked: res.checked ? 1 : 0,
            practice: state.fromPractice ? 1 : 0, dnot: state.dnot, detail: state.detail,
        }, more || {}), () => recentAdd());
        // ответ есть, но только численный (первообразной нет, предел по правилам не найден) — это тоже «не умею»
        if (res.soft) diag('numeric_only_' + state.mode, { mode: state.mode, kind }, state.src);
    } else {
        stage('input_refused', { reason: 'unsolved_' + kind });
        diag('unsolved_' + state.mode, { mode: state.mode, kind }, state.src);
    }
}

/* ── неявная и параметрическая функция ───────────────────────────────────── */
function refreshSpecial(sp, fromInput) {
    const v = sp.kind === 'param' ? sp.t : 'x';
    fillVars({ vars: [sp.kind === 'param' ? sp.t : 'x'], v: sp.kind === 'param' ? sp.t : 'x' });
    state.parsed = { node: sp.kind === 'param' ? sp.Y : sp.L, v, vars: [v], params: sp.params, warnings: sp.warnings, special: sp };
    // печать: у неявной y идёт после x («xy»), как в задачнике
    R = createRenderer({ t, locale: LOCALE, dnot: state.dnot, v: sp.kind === 'param' ? sp.t : 'y' });
    const box = $('calPreviewTex');
    box.innerHTML = '';
    box.appendChild(R.tex(specialTask(sp), false));
    showWarnings(state.parsed);
    $('calDetail').hidden = true;
    showResult(true);
    let res;
    try {
        res = sp.kind === 'implicit' ? runImplicit(sp) : runParam(sp);
    } catch (e) {
        failWith(tp('err_parse'), ['calInput'], false, e);
        caught(e, 'cal_solve_' + sp.kind, t('err_parse'));
        return;
    }
    state.result = res;
    flash($('calAnswer'));
    saveHash();
    report(res, fromInput, { order: state.order, params: sp.params.length });
}
function specialTask(sp) {
    const o = { locale: LOCALE, v: sp.kind === 'param' ? sp.t : 'y', raw: true };
    if (sp.kind === 'implicit') return toLatex(sp.L, o) + ' = ' + toLatex(sp.R, o);
    return '\\begin{cases} x = ' + toLatex(sp.X, o) + ' \\\\ y = ' + toLatex(sp.Y, o) + ' \\end{cases}';
}
function specialEnv(sp) {
    const env = {};
    sp.params.forEach((p, i) => { env[p] = 0.7 + ((p.charCodeAt(0) * 37 + i * 11) % 23) / 10; });
    return env;
}
/** Шаг решения: название, формула правила, строка «было = стало» и вложенный ход производной. */
function specialStep(title, formula, math, subOrders, subF) {
    const li = document.createElement('li');
    li.className = 'cal-step';
    const head = [R.append(Object.assign(document.createElement('span'), { className: 'cal-step-name' }), title)];
    if (formula) head.push(R.tex(formula, false));
    const h = R.line('cal-step-h', head);
    if (formula) h.lastChild.classList.add('cal-step-f');
    li.appendChild(h);
    if (math) {
        const m = document.createElement('div');
        m.className = 'cal-step-m';
        m.appendChild(R.tex(math, true));
        li.appendChild(m);
    }
    if (subOrders) {
        const r = R.derivative(subOrders, subF);
        r.el.classList.add('cal-sub');
        li.appendChild(r.el);
        return { li, text: r.text };
    }
    return { li, text: [] };
}
function runImplicit(sp) {
    const env = specialEnv(sp);
    const r = implicitDerivative(sp.L, sp.R, 'x', 'y', env);
    const o = { locale: LOCALE, v: 'y' };
    setTask(specialTask(sp), false);
    const notes = [paramsNote({ params: sp.params, v: 'x' })];
    if (state.order > 1) notes.push(tp('note_order_special'));
    setForms([], 'x');
    $('calStudyWrap').hidden = true;
    $('calTableWrap').hidden = true;
    if (!r.result) {
        setAnswer(t('answer_implicit'), null, '—');
        notes.push(tp('check_failed'));
        setNotes(notes);
        setCheck(false, '');
        $('calCheck').hidden = true;
        $('calStepsWrap').hidden = true;
        state.latex = [specialTask(sp)];
        return { ok: false, kind: 'implicit' };
    }
    const ansTex = "y' = " + toLatex(r.result, o);
    setAnswer(t('answer_implicit'), ansTex, null, 'y′ = ' + toText(r.result, o));
    setNotes(notes);
    setCheck(r.ok, r.ok ? tp('check_implicit', { n: r.check.tested }) : tp('check_failed'));
    const ol = document.createElement('ol');
    ol.className = 'cal-steps';
    const text = [];
    const FL = toLatex(r.F, o);
    const s1 = specialStep(t('s_move'), null, 'F(x, y) = ' + FL + ' = 0');
    ol.appendChild(s1.li);
    text.push('1. ' + lab('s_move') + 'F(x, y) = ' + toText(r.F, o) + ' = 0');
    const s2 = specialStep(t('s_partial_x'), null, "F'_x = " + toLatex(r.Fx, o), [r.dx], r.F);
    ol.appendChild(s2.li);
    text.push('2. ' + lab('s_partial_x') + 'F′x = ' + toText(r.Fx, o), ...s2.text.map((l) => '   ' + l));
    const s3 = specialStep(t('s_partial_y'), null, "F'_y = " + toLatex(r.Fy, o), [r.dy], r.F);
    ol.appendChild(s3.li);
    text.push('3. ' + lab('s_partial_y') + 'F′y = ' + toText(r.Fy, o), ...s3.text.map((l) => '   ' + l));
    const s4 = specialStep(t('s_implicit'), "y' = -\\dfrac{F'_x}{F'_y}", "y' = -\\dfrac{" + toLatex(r.Fx, o) + '}{' + toLatex(r.Fy, o) + '} = ' + toLatex(r.result, o));
    ol.appendChild(s4.li);
    text.push('4. ' + lab('s_implicit') + 'y′ = −F′x/F′y = ' + toText(r.result, o));
    const box = $('calSteps');
    box.innerHTML = '';
    box.appendChild(ol);
    state.text = text;
    $('calStepsWrap').hidden = false;
    state.latex = [specialTask(sp) + WSEP + ansTex];
    const pts = curvePoints(r.F, 'x', 'y', env, 24);
    renderPlot({ f: sp.L, v: 'x', curve: { kind: 'implicit', F: (x, y) => A.evaluate(r.F, Object.assign({}, env, { x, y })), pts } });
    return { ok: true, checked: r.ok, kind: 'implicit' };
}
function runParam(sp) {
    const env = specialEnv(sp);
    const tv = sp.t;
    const order = state.order >= 2 ? 2 : 1;
    const r = paramDerivative(sp.X, sp.Y, tv, order, env);
    const o = { locale: LOCALE, v: tv };
    setTask(specialTask(sp), false);
    const notes = [paramsNote({ params: sp.params, v: tv })];
    if (state.order > 2) notes.push(tp('note_order_special'));
    setForms([], tv);
    $('calStudyWrap').hidden = true;
    $('calTableWrap').hidden = true;
    if (!r.first) {
        setAnswer(t('answer_param'), null, '—');
        notes.push(r.reason === 'x_const' ? tp('err_param_x_const') : tp('check_failed'));
        setNotes(notes);
        setCheck(false, '');
        $('calCheck').hidden = true;
        $('calStepsWrap').hidden = true;
        state.latex = [specialTask(sp)];
        return { ok: false, kind: 'param' };
    }
    let ansTex = "y'_x = " + toLatex(r.first, o);
    let ansT = 'y′ₓ = ' + toText(r.first, o);
    if (r.second) { ansTex += WSEP + "y''_{xx} = " + toLatex(r.second, o); ansT = listJoin([ansT, 'y″ₓₓ = ' + toText(r.second, o)], NT); }
    setAnswer(t('answer_param'), ansTex, null, ansT);
    setNotes(notes);
    setCheck(r.ok, r.ok ? tp('check_param', { n: r.check.tested, ratio: ltr('Δy/Δx') }) : tp('check_failed'));
    const ol = document.createElement('ol');
    ol.className = 'cal-steps';
    const text = [];
    const s1 = specialStep(t('s_dx_t'), null, "x'_" + tv + ' = ' + toLatex(r.dX.result, o), [r.dX], sp.X);
    ol.appendChild(s1.li);
    text.push('1. ' + lab('s_dx_t') + 'x′ = ' + toText(r.dX.result, o), ...s1.text.map((l) => '   ' + l));
    const s2 = specialStep(t('s_dy_t'), null, "y'_" + tv + ' = ' + toLatex(r.dY.result, o), [r.dY], sp.Y);
    ol.appendChild(s2.li);
    text.push('2. ' + lab('s_dy_t') + 'y′ = ' + toText(r.dY.result, o), ...s2.text.map((l) => '   ' + l));
    const s3 = specialStep(t('s_param'), "y'_x = \\dfrac{y'_" + tv + "}{x'_" + tv + '}', "y'_x = \\dfrac{" + toLatex(r.dY.result, o) + '}{' + toLatex(r.dX.result, o) + '} = ' + toLatex(r.first, o));
    ol.appendChild(s3.li);
    text.push('3. ' + lab('s_param') + 'y′ₓ = ' + toText(r.first, o));
    if (r.second) {
        const s4 = specialStep(tp('s_param2', { yx: ltr('y′ₓ'), xt: ltr('x′' + tv) }), "y''_{xx} = \\dfrac{\\left(y'_x\\right)'_" + tv + "}{x'_" + tv + '}',
            "y''_{xx} = \\dfrac{" + toLatex(r.d1.result, o) + '}{' + toLatex(r.dX.result, o) + '} = ' + toLatex(r.second, o), [r.d1], r.first);
        ol.appendChild(s4.li);
        text.push('4. ' + tf('s_param2', { yx: 'y′ₓ', xt: 'x′' + tv }) + t('label_sep') + 'y″ₓₓ = ' + toText(r.second, o), ...s4.text.map((l) => '   ' + l));
    }
    const box = $('calSteps');
    box.innerHTML = '';
    box.appendChild(ol);
    state.text = text;
    $('calStepsWrap').hidden = false;
    state.latex = [specialTask(sp) + WSEP + ansTex];
    const Xf = A.compile(canon(sp.X), tv, env);
    const Yf = A.compile(canon(sp.Y), tv, env);
    const trig = A.some(sp.X, (m) => m.k === 'fn' && ['sin', 'cos', 'tan', 'cot'].includes(m.f)) || A.some(sp.Y, (m) => m.k === 'fn' && ['sin', 'cos', 'tan', 'cot'].includes(m.f));
    renderPlot({ f: sp.Y, v: tv, curve: { kind: 'param', fp: (tt) => [Xf(tt), Yf(tt)], tRange: trig ? [0, 2 * Math.PI] : [-3, 3] } });
    return { ok: true, checked: r.ok, kind: 'param' + order };
}

function failWith(msg, fields, fromInput, e) {
    const err = $('calError');
    err.innerHTML = '';
    R.append(err, msg);
    err.hidden = false;
    markInvalid(fields);
    showResult(false);
    if (fromInput) {
        // код отказа — латиницей до двоеточия («unexpected_char»), сам знак — в отвергнутой записи
        const code = String((e && e.code) || 'parse').split(':')[0].slice(0, 40);
        const where = fields && fields[0] && fields[0] !== 'calInput' ? fields[0].replace(/^cal/, '').toLowerCase() : 'expr';
        stage('input_refused', { reason: code });
        diag('parse_' + code, { mode: state.mode, field: where, at: e && typeof e.at === 'number' ? e.at : -1 }, where === 'expr' ? state.src : ($(fields[0]) || {}).value || '');
    }
}

function showResult(on) {
    // Пусто или запись не прочитана — на месте ответа шпаргалка записи.
    $('calEmpty').hidden = on;
    $('calCard').hidden = !on;
    $('calStepsWrap').hidden = !on;
    $('calPlotWrap').hidden = !on;
    if (!on) { $('calStudyWrap').hidden = true; $('calTableWrap').hidden = true; }
}

function flash(el) {
    if (!el) return;
    el.classList.remove('tc-flash');
    void el.offsetWidth;
    el.classList.add('tc-flash');
}

/* ── предпросмотр «как понято» ───────────────────────────────────────────── */
function taskLatex(q, opts = {}) {
    const f = toLatex(q.node, { raw: true, locale: LOCALE, v: q.v });
    const v = q.v;
    if (state.mode === 'derivative') {
        const n = state.order;
        if (state.dnot === 'leibniz') return (n > 1 ? '\\frac{d^{' + n + '}}{d' + v + '^{' + n + '}}' : '\\frac{d}{d' + v + '}') + '\\left(' + f + '\\right)';
        const primes = n <= 3 ? "'".repeat(n) : '^{(' + n + ')}';
        return '\\left(' + f + '\\right)' + primes;
    }
    if (state.mode === 'integral') {
        const body = q.node.k === 'add' ? '\\left(' + f + '\\right)' : f;
        if (state.kind === 'definite' && !opts.noBounds) return '\\int_{' + ptLatex(state.a) + '}^{' + ptLatex(state.b) + '} ' + body + '\\,d' + v;
        if (state.kind === 'area') return areaTask(q);
        return '\\int ' + body + '\\,d' + v;
    }
    if (state.mode === 'taylor') return 'f(' + v + ') = ' + f + WSEP + v + '_0 = ' + ptLatex(state.tayPoint || '0') + WSEP + 'n = ' + state.tayOrder;
    return '\\lim_{' + v + ' \\to ' + ptLatex(state.limPoint) + (ptIsInf(state.limPoint) ? '' : sideSuffixTex(state.side, NT)) + '} ' + f;
}
function ptIsInf(s) {
    try { return parsePoint(s || '0').k === 'inf'; } catch (_) { return false; }
}
/** Точка из поля — текстом для копии решения: «π/2», «+∞». */
function ptText(s) {
    try {
        const p = parsePoint(s || '0');
        if (p.k === 'inf') return p.sign < 0 ? '−∞' : '+∞';
        return toText(p, { raw: true, locale: LOCALE });
    } catch (_) { return String(s || ''); }
}
function ptLatex(s) {
    try {
        const p = parsePoint(s || '0');
        if (p.k === 'inf') return (p.sign < 0 ? '-' : '+') + '\\infty';
        return toLatex(p, { raw: true, locale: LOCALE });
    } catch (_) { return '?'; }
}

function showPreview(q) {
    const box = $('calPreviewTex');
    box.innerHTML = '';
    if (!q) return;
    box.appendChild(R.tex(taskLatex(q), false));
}

function showWarnings(q) {
    const w = $('calWarn');
    const msgs = [];
    for (const x of q.warnings || []) {
        if (x.code === 'warn_power_scope') msgs.push(tp('warn_power_scope', { src: ltr(snippet(q.clean, x.at)), fix: ltr('e^(2x)') }));
        if (x.code === 'warn_div_scope') msgs.push(tp('warn_div_scope', { src: ltr('1/2x'), read: ltr('(1/2)·x'), want: ltr('1/(2x)') }));
    }
    w.innerHTML = '';
    msgs.forEach((m, i) => R.append(w, i ? [' ', m] : m));
    w.hidden = !msgs.length;
}
function snippet(src, at) {
    const s = String(src || '');
    const a = Math.max(0, at - 2);
    return s.slice(a, at + 4).trim();
}

/** Список переменных: выбор, если букв больше одной. */
function fillVars(q) {
    const sel = $('calVar');
    const want = q.vars.length ? q.vars : ['x'];
    const cur = Array.from(sel.options).map((o) => o.value).join(',');
    if (cur !== want.join(',')) {
        sel.innerHTML = '';
        for (const v of want) {
            const o = document.createElement('option');
            o.value = v;
            o.textContent = v;
            sel.appendChild(o);
        }
    }
    sel.value = q.v;
    sel.disabled = want.length < 2;
}

/* ── ответ: общие части ──────────────────────────────────────────────────── */
function setTask(latex, inAnswer) {
    const el = $('calTask');
    el.innerHTML = '';
    el.appendChild(R.tex(latex, false));
    // Ответ интеграла и предела сам начинается с задания («∫ … dx = …») — строка
    // задания над ним была бы повтором.
    el.hidden = !!inAnswer;
}
function setAnswer(label, latex, textFallback, plain) {
    const box = $('calAnswer');
    box.innerHTML = '';
    state.lastText = plain || textFallback || '';
    const l = document.createElement('span');
    l.className = 'cal-ans-l';
    l.textContent = label;
    box.appendChild(l);
    if (latex) box.appendChild(R.tex(latex, true));
    else {
        const s = document.createElement('span');
        s.className = 'cal-ans-txt';
        s.textContent = textFallback || '';
        box.appendChild(s);
    }
}
/** Пустая заметка: '', [] или части без текста. */
const isEmptyMsg = (m) => !m || (Array.isArray(m) && !partText(m).trim());
/** Заметки под ответом: каждая — строка или части шаблона (значения изолированы). */
function setNotes(list) {
    const el = $('calNotes');
    el.innerHTML = '';
    const items = list.filter((m) => !isEmptyMsg(m));
    items.forEach((m, i) => R.append(el, i ? [' ', m] : m));
    el.hidden = !items.length;
}
function setCheck(ok, msg) {
    const el = $('calCheck');
    el.innerHTML = '';
    el.hidden = isEmptyMsg(msg);
    el.classList.toggle('bad', !ok);
    // строка — флекс (знак и фраза): фраза с записями внутри идёт ОДНИМ элементом, иначе каждый
    // кусок шаблона становится флекс-элементом — зазоры, перенос сбоку, в RTL запись рвётся
    if (!el.hidden) R.append(el, [R.append(document.createElement('span'), ok ? '✓' : '⚠'), R.append(document.createElement('span'), msg)]);
    const tx = $('calCheckTex');
    tx.innerHTML = '';
    tx.hidden = true;
    state.checkText = '';
}
/** Строка проверки формулой: «(F)′ = … = f» — как проверяют в тетради. */
function setCheckTex(labelKey, latex, text) {
    const tx = $('calCheckTex');
    tx.innerHTML = '';
    R.append(tx, R.labeled(labelKey, { tex: latex }));
    tx.hidden = false;
    state.checkText = lab(labelKey) + text;
}
function setForms(list, v) {
    const el = $('calForms');
    el.innerHTML = '';
    const extra = (list || []).filter((f) => f.id !== 'main').slice(0, 3);
    if (!extra.length) { el.hidden = true; return; }
    const h = document.createElement('span');
    h.textContent = lab('forms_title').trimEnd();
    el.appendChild(h);
    for (const f of extra) {
        const s = document.createElement('span');
        s.className = 'cal-forms-i';
        s.appendChild(document.createTextNode(t('form_' + f.id) + ' '));
        s.appendChild(R.tex(toLatex(f.n, { locale: LOCALE, v }), false));
        el.appendChild(s);
    }
    el.hidden = false;
}
function paramsNote(q) {
    if (!q.params.length) return '';
    return tp('note_params', { list: ltr(listJoin(q.params, NT)), v: ltr(q.v) });
}
function setSteps(el, textLines) {
    const box = $('calSteps');
    box.innerHTML = '';
    box.appendChild(el);
    state.text = textLines;
}

/* ── производная ─────────────────────────────────────────────────────────── */
function runDerivative(q) {
    const res = solveDerivative({ node: q.node, v: q.v, order: state.order, short: state.detail === 'short', point: state.point || null, params: q.params });
    const dLabel = state.order > 1 ? tf('answer_order', { n: state.order }) : t('answer_derivative');
    setTask(taskLatex(q));
    const rv = q.v;
    if (!res.ok) {
        setAnswer(dLabel, null, '—');
        setNotes([tp('check_failed')]);
        setCheck(false, tp('check_failed'));
        $('calStepsWrap').hidden = true;
        return { ok: false };
    }
    const fx = derivName(state.order, rv);
    let ans = fx + ' = ' + toLatex(res.result, { locale: LOCALE, v: rv });
    if (res.at) {
        ans += res.at.defined
            ? WSEP + derivName(state.order, toLatex(res.at.x0, { locale: LOCALE })) + ' = ' + toLatex(res.at.value, { locale: LOCALE }) + (isExactNum(res.at.value) ? '' : ' \\approx ' + fmtTex(res.at.x))
            : WSEP + derivName(state.order, toLatex(res.at.x0, { locale: LOCALE })) + labSepTex() + '\\text{—}';
    }
    const plainName = (state.order <= 3 ? 'f' + ['', '′', '″', '‴'][state.order] : 'f⁽' + state.order + '⁾') + '(' + rv + ')';
    let plain = plainName + ' = ' + toText(res.result, { locale: LOCALE, v: rv });
    if (res.at && res.at.defined) plain = listJoin([plain, plainName.replace('(' + rv + ')', '(' + toText(res.at.x0, { locale: LOCALE }) + ')') + ' = ' + toText(res.at.value, { locale: LOCALE })], NT);
    setAnswer(dLabel, ans, null, plain);
    const notes = [paramsNote(q)];
    if (res.at && !res.at.defined) notes.push(tp('answer_at_undefined'));
    setNotes(notes);
    const tested = Math.min(...res.checks.map((c) => c.tested));
    setCheck(res.verified, res.verified ? tp('check_derivative', { n: tested }) : tp('check_failed'));
    setForms(res.forms, rv);
    const r = R.derivative(res.orders, q.node);
    setSteps(r.el, r.text);
    state.latex = [taskLatex(q) + ' = ' + toLatex(res.result, { locale: LOCALE, v: rv })];
    const fp = res.orders[0].result;
    renderStudy(q, fp);
    $('calTableWrap').hidden = true;
    const study = state.studyRes || {};
    renderPlot({ f: q.node, v: rv, g: fp, gLabel: t('plot_fp'), tangent: study.tangent, extrema: study.mono ? study.mono.extrema : [] });
    const top = res.orders[0] && res.orders[0].steps && res.orders[0].steps[0];
    return { ok: true, checked: res.verified, kind: 'order' + state.order, rule: top && top.rule ? String(top.rule) : '' };
}
function derivName(n, v) {
    if (state.dnot === 'leibniz') return n > 1 ? '\\frac{d^{' + n + '}f}{d' + (/^[a-z]$/i.test(v) ? v : 'x') + '^{' + n + '}}' : '\\frac{df}{dx}';
    const primes = n <= 3 ? "'".repeat(n) : '^{(' + n + ')}';
    return 'f' + primes + '(' + v + ')';
}
function isExactNum(n) { return n.k === 'num'; }

function renderStudy(q, fp) {
    const wrap = $('calStudyWrap');
    const box = $('calStudy');
    const serr = $('calStudyErr');
    box.innerHTML = '';
    serr.hidden = true;
    state.studyRes = null;
    state.studyText = [];
    if (state.mode !== 'derivative') { wrap.hidden = true; return; }
    // Поля касательной и отрезка стоят в этой же панели: она видна всегда, когда
    // есть производная, иначе исправить неверную точку было бы негде.
    wrap.hidden = false;
    let st;
    try {
        st = studyDerivative({ node: q.node, v: q.v, params: q.params, tangentAt: state.tan || null, segA: state.segA || null, segB: state.segB || null }, fp);
    } catch (e) {
        if (e instanceof CalcParseError) {
            // какое поле не прочиталось — проверяем по одному
            const bad = [];
            for (const id of ['calTan', 'calSegA', 'calSegB']) {
                const val = $(id).value.trim();
                if (!val) continue;
                try { const p = parsePoint(val); if (p.k === 'inf') bad.push(id); } catch (_) { bad.push(id); }
            }
            markInvalid(bad.length ? bad : ['calTan']);
            serr.innerHTML = '';
            R.append(serr, errText(e));
            serr.hidden = false;
        }
        return;
    }
    state.studyRes = st;
    const L = (n) => toLatex(n, { locale: LOCALE, v: q.v });
    const T = (n) => toText(n, { locale: LOCALE, v: q.v });
    // Точное — через «=», найденное численно — через «≈»: «x ≈ 2,29», а не «x = ≈ 2,29».
    const xTex = (c) => (c.node ? L(c.node) : fmtTex(c.x));
    const xTxt = (c) => (c.node ? T(c.node) : fmt(c.x));
    const yTex = (c) => (c.y ? L(c.y) + (c.y.k === 'num' ? '' : ' \\approx ' + fmtTex(c.yx)) : fmtTex(c.yx));
    const yTxt = (c) => (c.y ? T(c.y) : fmt(c.yx));
    const eqX = (c) => q.v + (c.node ? ' = ' : ' \\approx ') + xTex(c);
    const eqXt = (c) => q.v + (c.node ? ' = ' : ' ≈ ') + xTxt(c);
    const eqY = (c, name) => name + (c.y ? ' = ' : ' \\approx ') + yTex(c);
    const eqYt = (c, name) => name + (c.y ? ' = ' : ' ≈ ') + yTxt(c);
    // перечни точек и промежутков — разделителем страны (при десятичной запятой «;»)
    const listX = (list) => listJoinTex(list.map(eqX), NT);
    const listXt = (list) => listJoin(list.map(eqXt), NT);
    const sec = (title) => {
        const d = document.createElement('div');
        const h = document.createElement('h4');
        h.textContent = title;
        d.appendChild(h);
        box.appendChild(d);
        state.studyText.push('', (title + t('label_sep')).trimEnd());
        return d;
    };
    /** Строка исследования: части на экран, текст — в копию решения. */
    const p = (parent, parts, text) => { parent.appendChild(R.line('cal-study-p', parts)); if (text) state.studyText.push(text); };
    /** «Подпись: формула» — экран и копия одним вызовом. */
    const pl = (parent, key, tex, txt) => p(parent, R.labeled(key, { tex }), lab(key) + txt);

    if (st.tangent) {
        const d = sec(t('tangent_title'));
        if (st.tangent.ok) {
            pl(d, 'tangent_line', 'y = ' + L(st.tangent.line), 'y = ' + T(st.tangent.line));
            if (st.tangent.normal) pl(d, 'normal_line', 'y = ' + L(st.tangent.normal), 'y = ' + T(st.tangent.normal));
            else pl(d, 'normal_vertical', q.v + ' = ' + L(st.tangent.x0), q.v + ' = ' + T(st.tangent.x0));
        } else p(d, tp('tangent_fail'), t('tangent_fail'));
    }
    if (st.segment) {
        const d = sec(t('seg_title'));
        const s = st.segment;
        if (!s.ok) {
            const msg = s.reason === 'segment' ? tp('err_segment')
                : s.reason === 'inf' ? tp('err_point', { ex: ltr(listJoin(['2', '−1/2', 'pi/4'], NT)) })
                    : tp('seg_fail', { at: ltr(fmt(s.at)) });
            p(d, msg, null);
            if (s.reason === 'segment' || s.reason === 'inf') markInvalid(['calSegA', 'calSegB']);
        } else {
            const ol = document.createElement('ol');
            const li = (parts, text) => { const x = document.createElement('li'); x.appendChild(R.line('', parts)); ol.appendChild(x); state.studyText.push('  ' + text); };
            const lit = (key, tex, txt) => li(R.labeled(key, { tex }), lab(key) + txt);
            lit('seg_s1', "f'(x) = " + L(fp), 'f′(x) = ' + T(fp));
            const crit = s.inside.concat(s.outside);
            // «Критические точки: f′(x) = 0 или не существует» — запись изолирована, слова — шаблоном
            li([...tp('seg_s2', { eq: { tex: "f'(x) = 0", txt: 'f′(x) = 0' } }), t('label_sep'), crit.length ? { tex: listX(crit) } : '—'],
                tf('seg_s2', { eq: 'f′(x) = 0' }) + t('label_sep') + (listXt(crit) || '—'));
            if (s.inside.length) lit('seg_s3', listX(s.inside), listXt(s.inside));
            else li(tp('seg_s3_none'), t('seg_s3_none'));
            lit('seg_s4', listJoinTex(s.candidates.map((c) => eqY(c, 'f\\left(' + xTex(c) + '\\right)')), NT),
                listJoin(s.candidates.map((c) => eqYt(c, 'f(' + xTxt(c) + ')')), NT));
            d.appendChild(ol);
            // значение может достигаться в нескольких точках — перечисляем все
            const atAll = (list) => '\\ \\left(' + listX(list) + '\\right)';
            const atAllT = (list) => ' (' + listXt(list) + ')';
            pl(d, 'seg_max', yTex(s.max) + atAll(s.maxAll), yTxt(s.max) + atAllT(s.maxAll));
            pl(d, 'seg_min', yTex(s.min) + atAll(s.minAll), yTxt(s.min) + atAllT(s.minAll));
        }
    }
    if (q.params.length) {
        const d = sec(t('mono_title'));
        p(d, tp('study_params'), t('study_params'));
    } else if (st.mono) {
        const m = st.mono;
        const d = sec(t('mono_title'));
        const crit = m.points.filter((c) => c.kind !== 'hole');
        // Колеблющаяся функция даёт десятки точек: сводка на виду, полный список — по нажатию.
        const many = crit.length > 6;
        const host = many ? document.createElement('details') : d;
        if (many) {
            const sm = document.createElement('summary');
            sm.className = 'cal-more';
            sm.appendChild(R.append(document.createElement('span'), [lab('crit_title'), ltr(crit.length)]));
            host.appendChild(sm);
            d.appendChild(host);
        }
        if (crit.length) pl(host, 'crit_title', listX(crit), listXt(crit));
        else p(host, R.labeled('crit_title', t('crit_none')), lab('crit_title') + t('crit_none'));
        const tbl = document.createElement('table');
        tbl.className = 'tc-table';
        const body = document.createElement('tbody');
        for (const iv of m.intervals) {
            if (iv.undefined) continue;
            const tr = document.createElement('tr');
            const a = document.createElement('td');
            a.appendChild(R.tex(intervalTex(iv, m.points), false));
            const b = document.createElement('td');
            b.className = iv.sign > 0 ? 'cal-up' : iv.sign < 0 ? 'cal-down' : '';
            // слово — перевод, «(f′ > 0)» — запись: в арабском абзаце она не выворачивается
            const word = iv.sign > 0 ? '↗ ' + t('mono_inc') : iv.sign < 0 ? '↘ ' + t('mono_dec') : t('mono_const');
            const sign = '(f′ ' + (iv.sign > 0 ? '> 0' : iv.sign < 0 ? '< 0' : '= 0') + ')';
            R.append(b, [word, ' ', ltr(sign)]);
            tr.appendChild(a);
            tr.appendChild(b);
            body.appendChild(tr);
            state.studyText.push('  ' + intervalText(iv, m.points) + t('label_sep') + word + ' ' + sign);
        }
        tbl.appendChild(body);
        const tw = document.createElement('div');
        tw.className = 'tc-tablewrap';
        tw.appendChild(tbl);
        host.appendChild(tw);
        if (m.extrema.length) {
            for (const e of m.extrema) {
                const key = e.type === 'max' ? 'extr_max' : 'extr_min';
                // «x = 0,5; f = 2» — разделителем страны и в формуле (с десятичной запятой «0,5, f» читается как число)
                pl(host, key, listJoinTex([eqX(e), eqY(e, 'f')], NT), listJoin([eqXt(e), eqYt(e, 'f')], NT));
            }
        } else p(d, tp('extr_none'), t('extr_none'));
        if (st.convex) {
            const c = st.convex;
            const d0 = sec(t('conv_title'));
            let d2 = d0;
            if (c.inflections.length > 6) {
                d2 = document.createElement('details');
                const sm = document.createElement('summary');
                sm.className = 'cal-more';
                sm.appendChild(R.append(document.createElement('span'), [lab('infl'), ltr(c.inflections.length)]));
                d2.appendChild(sm);
                d0.appendChild(d2);
            }
            if (c.inflections.length) {
                for (const e of c.inflections) pl(d2, 'infl', pointTex(xTex(e), yTex(e), NT), pointText(xTxt(e), yTxt(e), NT));
            } else p(d2, tp('infl_none'), t('infl_none'));
            const iv = c.intervals.filter((x) => !x.undefined && x.sign);
            if (iv.length) {
                const word = (x) => (x.sign > 0 ? t('conv_up') : t('conv_down'));
                p(d2, iv.flatMap((x, i) => [(i ? NT.listSep : ''), { tex: intervalTex(x, c.points) }, ' — ' + word(x)]),
                    listJoin(iv.map((x) => intervalText(x, c.points) + ' — ' + word(x)), NT));
            }
        }
        const note = [...tp('study_range', { a: ltr(fmt(m.range[0])), b: ltr(fmt(m.range[1])) }), ...(m.exact ? [] : [' ', ...tp('study_approx')])];
        p(box, note, tf('study_range', { a: fmt(m.range[0]), b: fmt(m.range[1]) }) + (m.exact ? '' : ' ' + t('study_approx')));
    }
}
function endTex(x, points) {
    if (x === -10) return '-\\infty';
    if (x === 10) return '+\\infty';
    const c = points.find((q) => Math.abs(q.x - x) < 1e-12);
    return c && c.node ? toLatex(c.node, { locale: LOCALE }) : fmtTex(x);
}
function endText(x, points) {
    if (x === -10) return '−∞';
    if (x === 10) return '+∞';
    const c = points.find((q) => Math.abs(q.x - x) < 1e-12);
    return c && c.node ? toText(c.node, { locale: LOCALE }) : fmt(x);
}
/** Промежуток монотонности/выпуклости — скобками страны: (a; b), (a, b), ]a; b[. */
function intervalTex(iv, pts) { return ivTex(endTex(iv.lo, pts), endTex(iv.hi, pts), NT, 'open'); }
function intervalText(iv, pts) { return ivText(endText(iv.lo, pts), endText(iv.hi, pts), NT, 'open'); }

/* ── интеграл ────────────────────────────────────────────────────────────── */
function runIntegral(q) {
    if (state.kind === 'area') return runArea(q);
    const definite = state.kind === 'definite';
    // Пустой предел интегрирования: раньше расчёт шёл как неопределённый, а показ ждал определённого —
    // исключение и «Не понял запись» про выражение, в котором ошибки нет.
    if (definite && (!state.a || !state.b)) throw Object.assign(new CalcParseError('bad_bound', 0), { field: state.a ? 'calB' : 'calA' });
    const res = solveIntegral({ node: q.node, v: q.v, params: q.params, a: definite ? state.a : null, b: definite ? state.b : null });
    setTask(taskLatex(q), true);
    const ind = res.indefinite;
    const notes = [paramsNote(q)];
    const v = q.v;
    let checked = false;
    const dx = '\\,d' + v;
    if (ind.ok) {
        const r = R.integral(ind);
        setSteps(r.el, r.text);
        $('calStepsWrap').hidden = false;
    } else {
        $('calSteps').innerHTML = '';
        state.text = [];
        $('calStepsWrap').hidden = true;
    }
    const intTex = '\\int ' + (q.node.k === 'add' ? '\\left(' + toLatex(q.node, { raw: true, locale: LOCALE }) + '\\right)' : toLatex(q.node, { raw: true, locale: LOCALE })) + dx;
    if (!definite) {
        if (ind.ok) {
            setAnswer(t('answer_integral'), intTex + ' = ' + toLatex(ind.F, { locale: LOCALE, v }) + ' + C', null, toText(ind.F, { locale: LOCALE, v }) + ' + C');
            notes.push(tp('answer_plus_c'));
            setCheck(true, tp('check_integral', { n: ind.check.tested }));
            checkByDiff(q, ind.F);
            checked = true;
            state.latex = [intTex + ' = ' + toLatex(ind.F, { locale: LOCALE, v }) + ' + C'];
        } else {
            setAnswer(t('answer_integral'), null, '—');
            notes.push(ind.nonElementary ? tp('note_nonelem', { name: t('sf_' + ind.nonElementary) }) : tp('note_int_unsolved'));
            setCheck(false, '');
            $('calCheck').hidden = true;
            state.latex = [intTex];
        }
        setNotes(notes);
        setForms([], v);
        $('calStudyWrap').hidden = true;
        $('calTableWrap').hidden = true;
        renderPlot({ f: q.node, v, g: ind.ok ? ind.F : null, gLabel: t('plot_F') });
        return { ok: ind.ok, checked, kind: ind.ok ? 'indefinite' : (ind.nonElementary ? 'nonelem' : 'unsolved'), rule: ind.ok ? ind.steps[0].rule : '' };
    }
    const d = res.definite;
    const defTex = taskLatex(q);
    let valTex = null;
    if (d.kind === 'newton_leibniz' || d.kind === 'improper') {
        valTex = toLatex(d.value, { locale: LOCALE });
        const exact = d.value.k === 'num';
        setAnswer(t('answer_definite'), defTex + ' = ' + valTex + (exact ? '' : ' \\approx ' + fmtTex(d.valueNum)), null, toText(d.value, { locale: LOCALE }) + (exact ? '' : ' ≈ ' + fmt(d.valueNum)));
        state.result = res;
        appendNewtonLeibniz(q, d);
        if (d.kind === 'improper') notes.push(tp('note_improper'));
        const ok = d.numeric.ok;
        setCheck(ok, ok ? tp('check_definite', { value: ltr(fmt(d.numeric.value)) }) : tp('check_failed'));
        checked = ok;
    } else if (d.kind === 'diverges') {
        // «= ∞» для ∫₋₁¹ dx/x — неправда (знака у расходимости нет): ответ — слово «расходится»
        setTask(defTex, false);
        setAnswer(t('answer_definite'), null, t('answer_diverges'));
        notes.push(tp('note_diverges'));
        if (d.splitAt) notes.push(tp('note_split', { at: ltr(listJoin(d.splitAt.map(pointName), NT)) }));
        $('calCheck').hidden = true;
    } else if (d.kind === 'split') {
        setAnswer(t('answer_definite'), defTex + ' \\approx ' + fmtTex(d.valueNum), null, '≈ ' + fmt(d.valueNum));
        notes.push(tp('note_split', { at: ltr(listJoin(d.splitAt.map(pointName), NT)) }));
        setCheck(d.numeric.ok, d.numeric.ok ? tp('check_definite', { value: ltr(fmt(d.numeric.value)) }) : tp('note_numeric_def'));
    } else {
        // численно: неберущийся, не найденный или Ньютон — Лейбниц неприменим
        if (d.numeric.ok) {
            const g = d.guess && d.guess.name !== '1' ? guessTex(d.guess) : null;
            setAnswer(t('answer_definite'), defTex + ' \\approx ' + fmtTex(d.numeric.value) + (g ? '\\quad \\left(' + g + '?\\right)' : ''), null, '≈ ' + fmt(d.numeric.value));
        } else setAnswer(t('answer_definite'), null, '—');
        // пределы уже заданы: совет «задайте пределы» из заметки для неопределённого интеграла здесь не к месту
        if (d.kind === 'mismatch') notes.push(tp('note_mismatch'));
        else if (ind.nonElementary) notes.push(tp('note_nonelem_def', { name: t('sf_' + ind.nonElementary) }));
        // прочерк и «значение посчитано численно» рядом — неправда: счёт не сошёлся, так и говорим;
        // «формула неприменима… посчитано численно» уже сказала это сама
        if (!d.numeric.ok) notes.push(tp('note_numeric_fail'));
        else if (d.kind !== 'mismatch') notes.push(tp('note_numeric_def'));
        $('calCheck').hidden = true;
    }
    setNotes(notes);
    setForms([], v);
    state.latex = [defTex + (valTex ? ' = ' + valTex : '')];
    $('calStudyWrap').hidden = true;
    $('calTableWrap').hidden = true;
    renderPlot({ f: q.node, v, area: { a: d.an, b: d.bn } });
    // «расходится» — это ответ; «только численно» — ответ с пометкой «точного нет»
    const numericOnly = !valTex && d.kind !== 'diverges' && d.kind !== 'split';
    return { ok: !!valTex || d.numeric.ok || d.kind === 'diverges', checked, kind: d.kind, soft: numericOnly && d.numeric.ok };
}
/** Особая точка в заметке: «π/2», «√2», а не «1,5708» — если число узнаётся. */
function pointName(x) {
    const g = identify(x);
    if (!g || g.name === '1' || Math.abs(g.p) > 12 || g.q > 12) return fmt(x);
    const a = Math.abs(g.p);
    return (g.p < 0 ? '−' : '') + (a === 1 ? '' : a) + g.name + (g.q !== 1 ? '/' + g.q : '');
}
/**
 * Проверка дифференцированием: (F)′ упрощаем и, если вышло не буква в букву
 * подынтегральное, дописываем «= f» — равенство уже подтверждено в 20 точках.
 */
function checkByDiff(q, F) {
    let dF = null;
    try { const d = diff(F, q.v); dF = d ? simplify(d, q.v) : null; } catch (_) { dF = null; }
    if (!dF) return;
    const o = { locale: LOCALE, v: q.v };
    const f = canon(q.node);
    let tex = '\\left(' + toLatex(F, o) + "\\right)' = " + toLatex(dF, o);
    let txt = '(' + toText(F, o) + ')′ = ' + toText(dF, o);
    if (A.key(canon(dF)) !== A.key(f)) { tex += ' = ' + toLatex(f, o); txt += ' = ' + toText(f, o); }
    setCheckTex('check_by_diff', tex, txt);
}

function guessTex(g) {
    const name = { 'π': '\\pi', 'e': 'e', '√2': '\\sqrt2', '√3': '\\sqrt3', '√5': '\\sqrt5', 'ln 2': '\\ln 2', 'ln 3': '\\ln 3', 'π²': '\\pi^2', '√π': '\\sqrt\\pi', 'e²': 'e^2' }[g.name] || g.name;
    const sign = g.p < 0 ? '-' : '';
    const p = Math.abs(g.p);
    const top = p === 1 ? name : p + name;
    return sign + (g.q === 1 ? top : '\\frac{' + top + '}{' + g.q + '}');
}
function appendNewtonLeibniz(q, d) {
    const box = $('calSteps');
    const ol = box.querySelector('ol.cal-steps') || document.createElement('ol');
    if (!ol.parentNode) { ol.className = 'cal-steps'; box.appendChild(ol); }
    const v = q.v;
    const o = { locale: LOCALE, v };
    const F = state.result && state.result.indefinite ? state.result.indefinite.F : null;
    const Fx = F ? toLatex(F, o) : 'F(' + v + ')';
    const Ft = F ? toText(F, o) : 'F(' + v + ')';
    // Подстановка как в тетради: (−cos x)|₀^π = (−cos π) − (−cos 0) = 1 − (−1) = 2.
    // На бесконечности и в особой точке вместо подстановки — предел.
    const raw = (pt, side) => {
        if (!F) return null;
        const p = parsePoint(pt);
        if (side.lim) return null;
        try { return A.subs(F, v, p); } catch (_) { return null; }
    };
    const rb = raw(state.b, d.Fb);
    const ra = raw(state.a, d.Fa);
    const Fb = d.Fb.lim ? '\\lim_{' + v + '\\to ' + ptLatex(state.b) + '} ' + Fx : (rb ? '\\left(' + toLatex(rb, { raw: true, locale: LOCALE }) + '\\right)' : 'F\\left(' + ptLatex(state.b) + '\\right)');
    const Fa = d.Fa.lim ? '\\lim_{' + v + '\\to ' + ptLatex(state.a) + '} ' + Fx : (ra ? '\\left(' + toLatex(ra, { raw: true, locale: LOCALE }) + '\\right)' : 'F\\left(' + ptLatex(state.a) + '\\right)');
    const li = document.createElement('li');
    li.className = 'cal-step';
    li.appendChild(R.line('cal-step-h', [
        Object.assign(document.createElement('span'), { className: 'cal-step-name', textContent: t('def_newton') }),
        R.tex('\\int_a^b f(x)\\,dx = F(b) - F(a)', false),
    ]));
    li.lastChild.lastChild.classList.add('cal-step-f');
    const m = document.createElement('div');
    m.className = 'cal-step-m';
    const bar = '\\left.' + (F && F.k === 'add' ? '\\left(' + Fx + '\\right)' : Fx) + '\\right|_{' + ptLatex(state.a) + '}^{' + ptLatex(state.b) + '}';
    // F(a) в скобках — только когда без них знак читался бы иначе: «− (−1)», но «− 0» и «− 1/2»
    const faL = toLatex(d.Fa.node, { locale: LOCALE });
    const faWrap = d.Fa.node.k === 'add' || faL.startsWith('-') ? '\\left(' + faL + '\\right)' : faL;
    m.appendChild(R.tex(bar + ' = ' + Fb + ' - ' + Fa + ' = ' + toLatex(d.Fb.node, { locale: LOCALE }) + ' - ' + faWrap + ' = ' + toLatex(d.value, { locale: LOCALE }), true));
    li.appendChild(m);
    ol.appendChild(li);
    const segT = ivText(ptText(state.a), ptText(state.b), NT, 'closed');
    state.text.push(lab('def_newton') + '(' + Ft + ')|' + segT + ' = ' + toText(d.Fb.node, { locale: LOCALE }) + ' − (' + toText(d.Fa.node, { locale: LOCALE }) + ') = ' + toText(d.value, { locale: LOCALE }));
    $('calStepsWrap').hidden = false;
}

/* ── площадь фигуры между линиями ────────────────────────────────────────── */
function areaG(q) {
    let g;
    try { g = parse(state.g || '0'); } catch (e) { e.field = 'calG'; throw e; }
    if (g.hasInf) throw Object.assign(new CalcParseError('inf_in_expr', 0), { field: 'calG' });
    return g.node;
}
function areaTask(q) {
    const o = { raw: true, locale: LOCALE, v: q.v };
    let g = null;
    try { g = parse(state.g || '0').node; } catch (_) { g = null; }
    const seg = state.areaA && state.areaB ? WSEP + q.v + ' \\in ' + ivTex(ptLatex(state.areaA), ptLatex(state.areaB), NT, 'closed') : '';
    // обозначение площади — по школе страны: S (ru), A (en, de) — ключ area_sym
    return areaSym() + '\\colon\\quad ' + listJoinTex(['y = ' + toLatex(q.node, o), 'y = ' + (g ? toLatex(g, o) : '?')], NT) + seg;
}
/** Обозначение площади в формулах: буква из перевода (S, A), латиница — как есть. */
function areaSym() {
    const s = String(t('area_sym') || 'S').trim();
    return /^[A-Za-z]$/.test(s) ? s : '\\text{' + s.replace(/[\\{}]/g, '') + '}';
}
function areaSymT() { return String(t('area_sym') || 'S').trim(); }
function runArea(q) {
    const v = q.v;
    const g = areaG(q);
    const hasSeg = !!(state.areaA && state.areaB);
    if (!!state.areaA !== !!state.areaB) throw Object.assign(new CalcParseError('area_bounds', 0), { field: state.areaA ? 'calAreaB' : 'calAreaA' });
    let aN = null;
    let bN = null;
    if (hasSeg) {
        try { aN = parsePoint(state.areaA); bN = parsePoint(state.areaB); } catch (e) { e.field = 'calAreaA'; throw e; }
        if (aN.k === 'inf' || bN.k === 'inf') throw Object.assign(new CalcParseError('bad_bound', 0), { field: 'calAreaA' });
    }
    const env = {};
    q.params.forEach((p, i) => { env[p] = 0.7 + ((p.charCodeAt(0) * 37 + i * 11) % 23) / 10; });
    const r = areaBetween(q.node, g, v, aN, bN, env);
    const lt = areaTask(q);
    setTask(lt, false);
    const notes = [paramsNote(q)];
    setForms([], v);
    $('calStudyWrap').hidden = true;
    $('calTableWrap').hidden = true;
    const o = { locale: LOCALE, v };
    if (!r.pieces) {
        setAnswer(t('answer_area'), null, '—');
        notes.push(r.reason === 'segment' ? tp('err_segment') : r.reason === 'not_continuous' ? tp('ar_not_cont', { at: ltr(fmt(r.at)) }) : tp('ar_few_points'));
        if (r.reason === 'segment') markInvalid(['calAreaA', 'calAreaB']);
        setNotes(notes);
        setCheck(false, '');
        $('calCheck').hidden = true;
        $('calSteps').innerHTML = '';
        state.text = [];
        $('calStepsWrap').hidden = true;
        state.latex = [lt];
        renderPlot({ f: q.node, v, g, gLabel: t('plot_g') });
        return { ok: false, kind: 'area_' + r.reason };
    }
    const exact = !!r.total;
    const valTex = exact ? toLatex(r.total, o) + (r.total.k === 'num' && r.total.r.d === 1n ? '' : ' \\approx ' + fmtTex(r.totalNum)) : '\\approx ' + fmtTex(r.totalNum);
    const valT = exact ? toText(r.total, o) + (r.total.k === 'num' && r.total.r.d === 1n ? '' : ' ≈ ' + fmt(r.totalNum)) : '≈ ' + fmt(r.totalNum);
    const S = areaSym();
    const ST = areaSymT();
    setAnswer(t('answer_area'), S + ' = ' + valTex, null, ST + ' = ' + valT);
    if (!exact) notes.push(tp('note_numeric_def'));
    setNotes(notes);
    setCheck(r.ok, r.ok ? tp('check_area', { value: ltr(fmt(r.check.value)), expr: ltr('|f − g|') }) : tp('check_failed'));

    const ptL = (p) => (p.node ? toLatex(p.node, o) : fmtTex(p.x));
    const ptT = (p) => (p.node ? toText(p.node, o) : fmt(p.x));
    const segL = (a, b) => ivTex(ptL(a), ptL(b), NT, 'closed');
    const segT = (a, b) => ivText(ptT(a), ptT(b), NT, 'closed');
    const fL = toLatex(q.node, o);
    const gL = toLatex(g, o);
    const fT = toText(q.node, o);
    const gT = toText(g, o);
    const ol = document.createElement('ol');
    ol.className = 'cal-steps';
    const text = [];
    const step = (title, formula, math, extraEl) => {
        const li = document.createElement('li');
        li.className = 'cal-step';
        const head = [R.append(Object.assign(document.createElement('span'), { className: 'cal-step-name' }), title)];
        if (formula) head.push(R.tex(formula, false));
        const h = R.line('cal-step-h', head);
        if (formula) h.lastChild.classList.add('cal-step-f');
        li.appendChild(h);
        if (math) { const m = document.createElement('div'); m.className = 'cal-step-m'; m.appendChild(R.tex(math, true)); li.appendChild(m); }
        if (extraEl) li.appendChild(extraEl);
        ol.appendChild(li);
    };
    const inner = r.pts.filter((p) => !p.end);
    const xs = (list) => listJoinTex(list.map((p) => v + (p.node ? ' = ' : ' \\approx ') + ptL(p)), NT);
    const xsT = (list) => listJoin(list.map((p) => v + (p.node ? ' = ' : ' ≈ ') + ptT(p)), NT);
    const last = r.pts[r.pts.length - 1];
    if (!hasSeg) {
        step(t('ar_points'), null, fL + ' = ' + gL + '\\;\\Rightarrow\\; ' + xs(r.pts));
        text.push('1. ' + lab('ar_points') + fT + ' = ' + gT + ' ⇒ ' + xsT(r.pts));
    } else {
        step(t('ar_points_seg'), null, v + ' \\in ' + segL(r.pts[0], last) + (inner.length ? WSEP + fL + ' = ' + gL + '\\;\\Rightarrow\\; ' + xs(inner) : ''));
        text.push('1. ' + lab('ar_points_seg') + segT(r.pts[0], last) + (inner.length ? NT.listSep + fT + ' = ' + gT + ' ⇒ ' + xsT(inner) : ''));
    }
    const upper = r.pieces.map((pc) => segL(pc.from, pc.to) + ':\\ ' + (pc.sign > 0 ? fL + ' \\ge ' + gL : gL + ' \\ge ' + fL));
    step(t('ar_upper'), null, upper.join(WSEP));
    text.push('2. ' + lab('ar_upper') + listJoin(r.pieces.map((pc) => segT(pc.from, pc.to) + t('label_sep') + (pc.sign > 0 ? fT + ' ≥ ' + gT : gT + ' ≥ ' + fT)), NT));
    const diffL = (pc) => (pc.sign > 0 ? '\\left(' + fL + ' - ' + (g.k === 'add' ? '\\left(' + gL + '\\right)' : gL) + '\\right)' : '\\left(' + gL + ' - ' + (q.node.k === 'add' ? '\\left(' + fL + '\\right)' : fL) + '\\right)');
    const Sint = r.pieces.map((pc) => '\\int_{' + ptL(pc.from) + '}^{' + ptL(pc.to) + '} ' + diffL(pc) + '\\,d' + v).join(' + ');
    step(t('ar_integral'), S + ' = \\int_a^b \\bigl(y_1(x) - y_2(x)\\bigr)\\,dx,\\ y_1 \\ge y_2', S + ' = ' + Sint);
    text.push('3. ' + lab('ar_integral') + ST + ' = ' + r.pieces.map((pc) => '∫' + segT(pc.from, pc.to) + ' (' + (pc.sign > 0 ? fT + ' − (' + gT + ')' : gT + ' − (' + fT + ')') + ') d' + v).join(' + '));
    if (r.F.ok) {
        const rr = R.integral(r.F);
        rr.el.classList.add('cal-sub');
        const hL = toLatex(r.h, o);
        step(t('ar_antider'), null, '\\int ' + (r.h.k === 'add' ? '\\left(' + hL + '\\right)' : hL) + '\\,d' + v + ' = ' + toLatex(r.F.F, o) + ' + C', rr.el);
        text.push('4. ' + lab('ar_antider') + 'F(' + v + ') = ' + toText(r.F.F, o), ...rr.text.map((l) => '   ' + l));
        const FL = toLatex(r.F.F, o);
        const bar = (pc) => (pc.sign > 0 ? '' : '-') + '\\left.' + (r.F.F.k === 'add' ? '\\left(' + FL + '\\right)' : FL) + '\\right|_{' + ptL(pc.from) + '}^{' + ptL(pc.to) + '}';
        const vals = r.pieces.map((pc) => (pc.value ? toLatex(pc.value, o) : '\\approx ' + fmtTex(pc.valueNum)));
        step(t('def_newton'), null, S + ' = ' + r.pieces.map(bar).join(' + ') + ' = ' + vals.join(' + ') + (r.pieces.length > 1 ? ' = ' + valTex : ''));
        text.push('5. ' + lab('def_newton') + ST + ' = ' + r.pieces.map((pc) => (pc.value ? toText(pc.value, o) : '≈ ' + fmt(pc.valueNum))).join(' + ') + ' = ' + valT);
    }
    const box = $('calSteps');
    box.innerHTML = '';
    box.appendChild(ol);
    state.text = text;
    $('calStepsWrap').hidden = false;
    state.latex = [lt + WSEP + S + ' = ' + valTex];
    renderPlot({ f: q.node, v, g, gLabel: t('plot_g'), between: { a: r.pts[0].x, b: r.pts[r.pts.length - 1].x }, window: [r.pts[0].x - Math.max(1, (r.pts[r.pts.length - 1].x - r.pts[0].x) * 0.35), r.pts[r.pts.length - 1].x + Math.max(1, (r.pts[r.pts.length - 1].x - r.pts[0].x) * 0.35)] });
    return { ok: true, checked: r.ok, kind: 'area' };
}

/* ── ряд Тейлора ─────────────────────────────────────────────────────────── */
/**
 * Многочлен Тейлора: таблица «k · f⁽ᵏ⁾(x) · f⁽ᵏ⁾(a) · коэффициент», затем
 * подстановка в формулу. Члены — по возрастанию степени, как в задачнике.
 */
function runTaylor(q) {
    const v = q.v;
    const aNode = parsePoint(state.tayPoint || '0');
    if (aNode.k === 'inf') throw new CalcParseError('point_inf', 0);
    const n = Math.max(1, Math.min(10, state.tayOrder));
    const env = {};
    for (const p of q.params) env[p] = 0.7 + p.charCodeAt(0) % 5 / 10;
    const r = taylor(q.node, v, aNode, n, env);
    const lt = taskLatex(q);
    setTask(lt, false);
    const o = { locale: LOCALE, v };
    const a0 = A.isZero(aNode);
    const label = a0 ? t('answer_maclaurin') : t('answer_taylor');
    const notes = [paramsNote(q)];
    setForms([], v);
    $('calStudyWrap').hidden = true;
    $('calTableWrap').hidden = true;
    if (!r.rows.length || r.reason) {
        setAnswer(label, null, '—');
        notes.push(r.reason === 'undefined' ? tp('tay_undefined') : tp('tay_no_derivative', { k: r.k || 0 }));
        setNotes(notes);
        setCheck(false, '');
        $('calCheck').hidden = true;
        $('calSteps').innerHTML = '';
        state.text = [];
        $('calStepsWrap').hidden = true;
        state.latex = [lt];
        renderPlot({ f: q.node, v });
        return { ok: false, kind: r.reason || 'fail' };
    }
    const shiftL = a0 ? v : '\\left(' + v + ' - ' + toLatex(aNode, o) + '\\right)';
    const shiftT = a0 ? v : '(' + v + ' − ' + toText(aNode, o) + ')';
    // Члены по возрастанию степени: знак члена — из его записи.
    const termsL = [];
    const termsT = [];
    for (const row of r.rows) {
        if (A.isZero(row.coef)) continue;
        if (row.k === 1 && !a0) {
            // Линейный член пишется «c(x − a)»: канон раскрыл бы его в «cx − ca»,
            // и в многочлене Тейлора пропала бы степень (x − a).
            const cL = toLatex(row.coef, o);
            const cT = toText(row.coef, o);
            const wrapL = row.coef.k === 'add' ? '\\left(' + cL + '\\right)' : cL;
            const wrapT = row.coef.k === 'add' || /[/\s]/.test(cT) ? '(' + cT + ')' : cT;
            termsL.push((A.isOne(row.coef) ? '' : A.isMinusOne(row.coef) ? '-' : wrapL) + shiftL);
            termsT.push((A.isOne(row.coef) ? '' : A.isMinusOne(row.coef) ? '−' : wrapT) + shiftT);
            continue;
        }
        const tNode = row.k === 0 ? row.coef : mulC([row.coef, row.k === 1 ? r.shift : powC(r.shift, A.int(row.k))]);
        termsL.push(toLatex(tNode, o));
        termsT.push(toText(tNode, o));
    }
    const joinL = (list) => list.map((x, i) => (i === 0 ? x : (x.startsWith('-') ? ' - ' + x.slice(1) : ' + ' + x))).join('') || '0';
    const joinT = (list) => list.map((x, i) => (i === 0 ? x : (/^[−-]/.test(x) ? ' − ' + x.replace(/^[−-]\s*/, '') : ' + ' + x))).join('') || '0';
    const rem = 'o\\left(' + (a0 ? v + '^{' + n + '}' : shiftL + '^{' + n + '}') + '\\right)';
    const remT = 'o(' + shiftT + (n > 1 ? '^' + n : '') + ')';
    const fL = toLatex(q.node, { raw: true, locale: LOCALE });
    const ansTex = fL + ' = ' + joinL(termsL) + ' + ' + rem;
    setAnswer(label, ansTex, null, joinT(termsT) + ' + ' + remT);
    setNotes(notes);
    // «(x − a)ⁿ⁺¹» — запись, а не слова: подставляется изолированной
    setCheck(r.ok, r.ok ? tp('check_taylor', { n: n + 1, rem: ltr('(x − a)ⁿ⁺¹') }) : tp('check_failed'));

    // Ход решения: таблица производных и значений, затем формула.
    const box = $('calSteps');
    box.innerHTML = '';
    const text = [];
    const ol = document.createElement('ol');
    ol.className = 'cal-steps';
    const li1 = document.createElement('li');
    li1.className = 'cal-step';
    li1.appendChild(R.line('cal-step-h', [Object.assign(document.createElement('span'), { className: 'cal-step-name', textContent: t('tay_derivs') })]));
    const tw = document.createElement('div');
    tw.className = 'tc-tablewrap';
    const tbl = document.createElement('table');
    tbl.className = 'tc-table cal-tay';
    const head = document.createElement('thead');
    const hr = document.createElement('tr');
    for (const h of ['k', 'f^{(k)}(' + v + ')', 'f^{(k)}\\left(' + toLatex(aNode, o) + '\\right)', '\\dfrac{f^{(k)}(a)}{k!}']) {
        const th = document.createElement('th');
        th.appendChild(R.tex(h, false));
        hr.appendChild(th);
    }
    head.appendChild(hr);
    tbl.appendChild(head);
    const body = document.createElement('tbody');
    text.push('1. ' + lab('tay_derivs').trimEnd());
    for (const row of r.rows) {
        const tr = document.createElement('tr');
        const kName = row.k === 0 ? 'f' : row.k <= 3 ? 'f' + "'".repeat(row.k) : 'f^{(' + row.k + ')}';
        const cells = [String(row.k), kName + '(' + v + ') = ' + toLatex(row.deriv, o), toLatex(row.value, o), toLatex(row.coef, o)];
        cells.forEach((c, i) => {
            const td = document.createElement('td');
            if (i === 0) td.textContent = c; else td.appendChild(R.tex(c, false));
            tr.appendChild(td);
        });
        body.appendChild(tr);
        text.push('   k = ' + row.k + t('label_sep') + listJoin(['f⁽' + row.k + '⁾(' + v + ') = ' + toText(row.deriv, o), 'f⁽' + row.k + '⁾(' + toText(aNode, o) + ') = ' + toText(row.value, o), 'c = ' + toText(row.coef, o)], NT));
    }
    tbl.appendChild(body);
    tw.appendChild(tbl);
    li1.appendChild(tw);
    ol.appendChild(li1);
    const li2 = document.createElement('li');
    li2.className = 'cal-step';
    const h2 = R.line('cal-step-h', [
        Object.assign(document.createElement('span'), { className: 'cal-step-name', textContent: t('tay_formula') }),
        R.tex('f(x) = \\sum_{k=0}^{n} \\frac{f^{(k)}(a)}{k!}(x - a)^k + o\\left((x - a)^n\\right)', false),
    ]);
    h2.lastChild.classList.add('cal-step-f');
    li2.appendChild(h2);
    const m = document.createElement('div');
    m.className = 'cal-step-m';
    m.appendChild(R.tex(ansTex, true));
    li2.appendChild(m);
    ol.appendChild(li2);
    text.push('2. ' + lab('tay_formula') + toText(q.node, { raw: true, locale: LOCALE }) + ' = ' + joinT(termsT) + ' + ' + remT);
    box.appendChild(ol);
    state.text = text;
    $('calStepsWrap').hidden = false;
    state.latex = [ansTex];
    let x0 = 0;
    try { x0 = A.evaluate(aNode, {}); } catch (_) { x0 = 0; }
    renderPlot({ f: q.node, v, g: r.poly, gLabel: tf('plot_T', { n }), window: [x0 - 3, x0 + 3] });
    return { ok: true, checked: r.ok, kind: 'taylor' + n };
}

/* ── предел ──────────────────────────────────────────────────────────────── */
function runLimit(q) {
    const res = solveLimit({ node: q.node, v: q.v, params: q.params, point: state.limPoint || '0', side: state.side });
    const L = res.limit;
    const lt = taskLatex(q);
    setTask(lt, L.kind !== 'dne' && L.kind !== 'unknown');
    const notes = [paramsNote(q)];
    let ansTex = null;
    let checked = false;
    if (L.domainSide) notes.push(L.domainSide > 0 ? tp('note_domain_right') : tp('note_domain_left'));
    if (L.kind === 'finite') {
        const exact = L.value.k === 'num';
        ansTex = lt + ' = ' + toLatex(L.value, { locale: LOCALE }) + (exact ? '' : ' \\approx ' + fmtTex(A.evaluate(L.value, {})));
    } else if (L.kind === '+inf' || L.kind === '-inf') {
        ansTex = lt + ' = ' + (L.kind === '+inf' ? '+\\infty' : '-\\infty');
    } else if (L.kind === 'dne') {
        setAnswer(t('answer_limit'), null, t('lim_dne'));
        const known = (sd) => !!sd && (sd.kind === 'finite' || sd.kind === '+inf' || sd.kind === '-inf');
        if (L.sides && (known(L.sides.left) || known(L.sides.right))) notes.push(tp('lim_dne_sides', { left: ltr(sideValue(L.sides.left)), right: ltr(sideValue(L.sides.right)) }));
        else if (L.sides || L.reason === 'oscillates') notes.push(tp('lim_oscillates'));
    }
    if (ansTex) setAnswer(t('answer_limit'), ansTex, null, L.kind === 'finite' ? toText(L.value, { locale: LOCALE }) : (L.kind === '+inf' ? '+∞' : '−∞'));
    if (L.kind === 'unknown') {
        const n = res.numeric;
        if (n && n.kind === 'finite') {
            const g = n.guess && n.guess.name !== '1' ? guessTex(n.guess) : null;
            setAnswer(t('answer_limit'), lt + ' \\approx ' + fmtTex(n.value) + (g ? '\\quad\\left(' + g + '?\\right)' : ''), null, '≈ ' + fmt(n.value));
            notes.push(tp('note_lim_unsolved'), tp('note_lim_numeric', { value: ltr(fmt(n.value)) }));
        } else {
            setAnswer(t('answer_limit'), null, '—');
            notes.push(tp('note_lim_unsolved'));
        }
    }
    setNotes(notes);
    const nm = L.numeric || res.numeric;
    if (L.kind !== 'unknown') {
        if (L.ok && nm && nm.kind === 'finite') { setCheck(true, tp('check_limit', { value: ltr(fmt(nm.value)) })); checked = true; }
        else if (L.ok && nm && (nm.kind === '+inf' || nm.kind === '-inf')) { setCheck(true, tp('check_limit_inf')); checked = true; }
        else if (L.ok) $('calCheck').hidden = true;
        else setCheck(false, tp('check_failed'));
    } else $('calCheck').hidden = true;
    setForms([], q.v);
    if (L.kind !== 'unknown' && (L.steps.length || L.sides)) {
        const r = R.limit(L, q.v, parsePoint(state.limPoint || '0'), L.domainSide || state.side);
        setSteps(r.el, r.text);
        $('calStepsWrap').hidden = false;
    } else { $('calSteps').innerHTML = ''; state.text = []; $('calStepsWrap').hidden = true; }
    state.latex = [ansTex || lt];
    renderTable(nm, q);
    $('calStudyWrap').hidden = true;
    let a0 = 0;
    try { const p = parsePoint(state.limPoint || '0'); a0 = p.k === 'inf' ? p.sign * Infinity : A.evaluate(p, {}); } catch (_) { a0 = 0; }
    renderPlot({ f: q.node, v: q.v, limitAt: a0, limitValue: L.kind === 'finite' ? A.evaluate(L.value, {}) : null });
    const numericOnly = L.kind === 'unknown' && !!res.numeric && res.numeric.kind === 'finite';
    return { ok: L.kind !== 'unknown' || numericOnly, soft: numericOnly, checked, kind: L.kind, rule: L.kind !== 'unknown' && L.steps && L.steps.length ? String((L.steps.find((x) => x.rule !== 'indeterminate' && x.rule !== 'substitute') || L.steps[0]).rule) : '' };
}
/** Значение одностороннего предела — запись для фразы «слева −∞, справа +∞». */
function sideValue(sd) {
    if (!sd) return '?';
    if (sd.kind === 'finite') return toText(sd.value, { locale: LOCALE });
    if (sd.kind === '+inf') return '+∞';
    if (sd.kind === '-inf') return '−∞';
    return '—';
}

/**
 * Таблица приближения: слева и справа от точки — рядом, по пять строк
 * (0,1 … 0,00001 — дальше уже потеря точности и запись 1e−6, которой нет в школе).
 */
function renderTable(nm, q) {
    const wrap = $('calTableWrap');
    const tbl = $('calTable');
    tbl.innerHTML = '';
    tbl.className = 'tc-table cal-lt';
    if (!nm || !nm.table || !nm.table.length) { wrap.hidden = true; return; }
    const Ls = nm.table.filter((p) => p.side === -1).slice(0, 5);
    const Rs = nm.table.filter((p) => p.side === 1).slice(0, 5);
    const one = nm.table.filter((p) => p.side !== -1 && p.side !== 1).slice(0, 6);
    const cell = (tag, cls, txt, span) => '<' + tag + (cls ? ' class="' + cls + '"' : '') + (span ? ' colspan="' + span + '"' : '') + '>' + esc(txt) + '</' + tag + '>';
    const yv = (p) => (p && Number.isFinite(p.y) ? fmt(p.y) : '—');
    const gap = (tag) => '<' + tag + ' class="cal-lt-gap"></' + tag + '>';
    const head = document.createElement('thead');
    const body = document.createElement('tbody');
    if (Ls.length && Rs.length) {
        head.innerHTML = '<tr>' + cell('th', 'cal-lt-side', t('side_left'), 2) + gap('th') + cell('th', 'cal-lt-side', t('side_right'), 2) + '</tr>'
            + '<tr>' + cell('th', 'num', q.v) + cell('th', 'num', t('table_fx')) + gap('th') + cell('th', 'num', q.v) + cell('th', 'num', t('table_fx')) + '</tr>';
        for (let i = 0; i < Math.max(Ls.length, Rs.length); i++) {
            const a = Ls[i];
            const b = Rs[i];
            const tr = document.createElement('tr');
            tr.innerHTML = cell('td', 'num', a ? fmt(a.x) : '') + cell('td', 'num', a ? yv(a) : '') + gap('td')
                + cell('td', 'num', b ? fmt(b.x) : '') + cell('td', 'num', b ? yv(b) : '');
            body.appendChild(tr);
        }
    } else {
        const list = Ls.length ? Ls : Rs.length ? Rs : one;
        head.innerHTML = '<tr>' + cell('th', 'num', q.v) + cell('th', 'num', t('table_fx')) + '</tr>';
        for (const p of list) {
            const tr = document.createElement('tr');
            tr.innerHTML = cell('td', 'num', fmt(p.x)) + cell('td', 'num', yv(p));
            body.appendChild(tr);
        }
    }
    tbl.appendChild(head);
    tbl.appendChild(body);
    wrap.hidden = false;
}
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

/* ── график ──────────────────────────────────────────────────────────────── */
const COLORS = { f: null, g: '#f59e0b', tan: '#34d399', area: null };
let lastPlot = null;
function themeColors() {
    const css = getComputedStyle(document.documentElement);
    const g = (n, d) => (css.getPropertyValue(n) || '').trim() || d;
    const light = document.documentElement.getAttribute('data-theme') === 'light';
    return {
        ink: g('--text-primary', light ? '#111827' : '#e6eaf0'),
        muted: g('--text-muted', '#98a3b0'),
        grid: g('--border', light ? '#d5dae1' : '#2b333c'),
        bg: g('--bg-secondary', light ? '#ffffff' : '#12161b'),
        panel: g('--bg-primary', light ? '#ffffff' : '#0b0f14'),
        accent: (getComputedStyle($('calApp')).getPropertyValue('--lg-accent') || '').trim() || '#7aa7ff',
    };
}

function renderPlot(spec) {
    lastPlot = spec;
    const wrap = $('calPlotWrap');
    const cv = $('calPlot');
    if (!spec || wrap.hidden) return;
    if (spec.curve) { renderCurve(spec.curve, cv); return; }
    const envP = paramEnvLocal(spec.f);
    const f = A.compile(canon(spec.f), spec.v, envP);
    const g = spec.g ? A.compile(spec.g, spec.v, envP) : null;
    // окно по x: вокруг интересных точек
    const xs = [];
    // экстремумы вдали (у x²·sin x их десятки) окно не растягивают: кривая сплющилась бы
    if (spec.extrema) for (const e of spec.extrema) if (Math.abs(e.x) <= 8) xs.push(e.x);
    if (spec.tangent && spec.tangent.ok) xs.push(A.evaluate(spec.tangent.x0, {}));
    if (spec.area) { if (Number.isFinite(spec.area.a)) xs.push(spec.area.a); if (Number.isFinite(spec.area.b)) xs.push(spec.area.b); }
    let x0 = -5;
    let x1 = 5;
    if (spec.limitAt !== undefined) {
        if (Number.isFinite(spec.limitAt)) { x0 = spec.limitAt - 3; x1 = spec.limitAt + 3; } else if (spec.limitAt > 0) { x0 = -1; x1 = 30; } else { x0 = -30; x1 = 1; }
    }
    const fin = xs.filter(Number.isFinite);
    if (fin.length) {
        // Окно — вокруг найденных точек (экстремумы, точка касания, пределы
        // интегрирования): там всё, ради чего смотрят на график.
        const lo = Math.min(...fin);
        const hi = Math.max(...fin);
        const pad = Math.max(2, (hi - lo) * 0.6);
        x0 = lo - pad;
        x1 = hi + pad;
    }
    if (spec.window) { x0 = spec.window[0]; x1 = spec.window[1]; }
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    const rect = cv.getBoundingClientRect();
    const w = Math.max(280, Math.round(rect.width || 640));
    const h = Math.max(180, Math.round(rect.height || 260));
    cv.width = Math.round(w * dpr);
    cv.height = Math.round(h * dpr);
    const ctx = cv.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // окно по y: по значениям функции без выбросов
    const ys = [];
    for (let i = 0; i <= 400; i++) {
        const x = x0 + (x1 - x0) * i / 400;
        for (const fn of [f, g]) {
            if (!fn) continue;
            const y = fn(x);
            if (Number.isFinite(y) && Math.abs(y) < 1e6) ys.push(y);
        }
    }
    ys.sort((a, b) => a - b);
    // устойчивые квантили: крутые края (x³ у края окна) не сплющивают середину
    const cut = Math.floor(ys.length * 0.08);
    let y0 = ys.length ? ys[cut] : -5;
    let y1 = ys.length ? ys[ys.length - 1 - cut] : 5;
    if (spec.limitValue != null && Number.isFinite(spec.limitValue)) { y0 = Math.min(y0, spec.limitValue); y1 = Math.max(y1, spec.limitValue); }
    for (const e of spec.extrema || []) if (Number.isFinite(e.yx) && e.x >= x0 && e.x <= x1) { y0 = Math.min(y0, e.yx); y1 = Math.max(y1, e.yx); }
    const span = Math.max(1e-6, y1 - y0);
    y0 -= span * 0.15;
    y1 += span * 0.15;
    if (y0 > 0) y0 = -span * 0.1;
    if (y1 < 0) y1 = span * 0.1;
    const th = themeColors();
    const rows = [{ kind: 'explicit', f, color: th.accent, label: 'f' }];
    if (g) rows.push({ kind: 'explicit', f: g, color: COLORS.g, label: spec.gLabel });
    const poi = [];
    if (spec.extrema) for (const e of spec.extrema) if (Number.isFinite(e.yx)) poi.push({ id: 'e' + e.x, x: e.x, y: e.yx, color: th.accent });
    if (spec.limitAt !== undefined && Number.isFinite(spec.limitAt) && spec.limitValue != null && Number.isFinite(spec.limitValue)) {
        poi.push({ id: 'lim', x: spec.limitAt, y: spec.limitValue, color: COLORS.g });
    }
    const scene = {
        w, h, view: { x0, x1, y0, y1 }, theme: th, rows, poi, background: false, legend: false, fmtNum: axisNum,
    };
    if (spec.tangent && spec.tangent.ok) {
        const k = A.evaluate(spec.tangent.k, envP);
        const x0t = A.evaluate(spec.tangent.x0, envP);
        const y0t = A.evaluate(spec.tangent.y0, envP);
        if (Number.isFinite(k) && Number.isFinite(y0t)) scene.tangent = { k, b: y0t - k * x0t, color: COLORS.tan };
    }
    if (spec.area && Number.isFinite(spec.area.a) && Number.isFinite(spec.area.b)) scene.integral = { a: spec.area.a, b: spec.area.b, f, color: th.accent };
    try { paint(ctx, scene); } catch (e) { console.error('[calculus-solver] plot', e); }
    if (spec.between && g) {
        // площадь между линиями — полупрозрачная заливка между f и g
        const M = viewMap(scene.view, w, h);
        const n = 300;
        const { a, b } = spec.between;
        ctx.save();
        ctx.fillStyle = th.accent;
        ctx.globalAlpha = 0.22;
        ctx.beginPath();
        for (let i = 0; i <= n; i++) { const x = a + (b - a) * i / n; const y = f(x); ctx[i ? 'lineTo' : 'moveTo'](M.sx(x), M.sy(Number.isFinite(y) ? y : 0)); }
        for (let i = n; i >= 0; i--) { const x = a + (b - a) * i / n; const y = g(x); ctx.lineTo(M.sx(x), M.sy(Number.isFinite(y) ? y : 0)); }
        ctx.closePath();
        ctx.fill();
        ctx.restore();
    }
    // легенда
    const lg = $('calLegend');
    const items = [['f', th.accent, false, t('plot_f')]];
    if (g) items.push([spec.gLabel, COLORS.g, false, spec.gLabel]);
    if (scene.tangent) items.push([t('plot_tangent'), COLORS.tan, true, t('plot_tangent')]);
    lg.innerHTML = items.map((it) => '<span><i class="' + (it[2] ? 'dash' : '') + '" style="background:' + esc(it[1]) + ';color:' + esc(it[1]) + '"></i>' + esc(it[3]) + '</span>').join('');
    lg.hidden = false;
}
/** Кривая, заданная неявно или параметрически: окно — по её точкам. */
function renderCurve(c, cv) {
    let xs = [];
    let ys = [];
    if (c.kind === 'implicit') { xs = c.pts.map((p) => p.x); ys = c.pts.map((p) => p.y); } else {
        for (let i = 0; i <= 400; i++) {
            const p = c.fp(c.tRange[0] + (c.tRange[1] - c.tRange[0]) * i / 400);
            if (p && Number.isFinite(p[0]) && Number.isFinite(p[1]) && Math.abs(p[0]) < 1e3 && Math.abs(p[1]) < 1e3) { xs.push(p[0]); ys.push(p[1]); }
        }
    }
    const span = (arr, d) => (arr.length ? [Math.min(...arr), Math.max(...arr)] : d);
    let [x0, x1] = span(xs, [-5, 5]);
    let [y0, y1] = span(ys, [-5, 5]);
    const m = Math.max(x1 - x0, y1 - y0, 2) * 0.15;
    x0 -= m; x1 += m; y0 -= m; y1 += m;
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    const rect = cv.getBoundingClientRect();
    const w = Math.max(280, Math.round(rect.width || 640));
    const h = Math.max(180, Math.round(rect.height || 260));
    // Одинаковый масштаб по осям — окружность круг, а не эллипс; но у вытянутой
    // кривой (x = t², y = t³) он сплющил бы её в линию — тогда оси независимы.
    const cx = (x0 + x1) / 2;
    const cy = (y0 + y1) / 2;
    const k = Math.max((x1 - x0) / w, (y1 - y0) / h);
    const stretch = Math.max((x1 - x0) / (y1 - y0), (y1 - y0) / (x1 - x0));
    if (stretch < 4) { x0 = cx - k * w / 2; x1 = cx + k * w / 2; y0 = cy - k * h / 2; y1 = cy + k * h / 2; }
    cv.width = Math.round(w * dpr);
    cv.height = Math.round(h * dpr);
    const ctx = cv.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const th = themeColors();
    const row = c.kind === 'implicit' ? { kind: 'implicit', op: '=', F: c.F, color: th.accent, label: 'F' } : { kind: 'param', fp: c.fp, tRange: c.tRange, color: th.accent, label: 'C' };
    try { paint(ctx, { w, h, view: { x0, x1, y0, y1 }, theme: th, rows: [row], poi: [], background: false, legend: false, fmtNum: axisNum }); } catch (e) { console.error('[calculus-solver] plot', e); }
    const lg = $('calLegend');
    lg.innerHTML = '<span><i style="background:' + esc(th.accent) + ';color:' + esc(th.accent) + '"></i>' + esc(t('plot_curve')) + '</span>';
    lg.hidden = false;
}

function paramEnvLocal(node) {
    const env = {};
    let i = 0;
    for (const n of A.varsOf(node)) {
        if (state.parsed && n === state.parsed.v) continue;
        env[n] = 0.7 + ((n.charCodeAt(0) * 37 + i * 11) % 23) / 10;
        i++;
    }
    return env;
}

/* ── адрес: задача передаётся ссылкой ────────────────────────────────────── */
function saveHash() {
    const q = new URLSearchParams();
    q.set('m', state.mode[0]);
    q.set('f', state.src);
    if (state.mode === 'derivative') {
        if (state.order > 1) q.set('n', String(state.order));
        if (state.point) q.set('p', state.point);
        if (state.tan) q.set('tx', state.tan);
        if (state.segA && state.segB) { q.set('sa', state.segA); q.set('sb', state.segB); }
        if (state.detail === 'full') q.set('d', 'full');
    } else if (state.mode === 'integral') {
        if (state.kind === 'definite') { q.set('a', state.a); q.set('b', state.b); }
        if (state.kind === 'area') { q.set('k', 'area'); q.set('g', state.g || '0'); if (state.areaA && state.areaB) { q.set('aa', state.areaA); q.set('ab', state.areaB); } }
    } else if (state.mode === 'limit') {
        q.set('at', state.limPoint);
        if (state.side) q.set('s', String(state.side));
    } else {
        q.set('ta', state.tayPoint || '0');
        q.set('tn', String(state.tayOrder));
    }
    if (state.parsed && state.parsed.v !== 'x') q.set('v', state.parsed.v);
    try { history.replaceState(null, '', '#' + q.toString()); } catch (_) { /* песочница */ }
}
function loadHash() {
    const h = String(location.hash || '').replace(/^#/, '');
    if (!h) return false;
    const q = new URLSearchParams(h);
    const f = q.get('f');
    if (!f) return false;
    const m = { d: 'derivative', i: 'integral', l: 'limit', t: 'taylor' }[q.get('m')] || 'derivative';
    // ссылка описывает задачу целиком: чего в ней нет — то по умолчанию, а не «как было у прошлой задачи»
    // (после определённого интеграла ссылка на неопределённый открывалась с прежними пределами)
    setMode(m, { silent: true });
    setKind('indefinite');
    setSide(0);
    $('calOrder').value = '1';
    for (const id of ['calPoint', 'calTan', 'calSegA', 'calSegB']) $(id).value = '';
    $('calInput').value = f;
    if (q.get('n')) $('calOrder').value = q.get('n');
    if (q.get('p')) $('calPoint').value = q.get('p');
    if (q.get('tx')) $('calTan').value = q.get('tx');
    if (q.get('sa')) { $('calSegA').value = q.get('sa'); $('calSegB').value = q.get('sb') || ''; }
    if (q.get('d') === 'full') setDetail('full', true);
    if (q.get('a') !== null) { $('calA').value = q.get('a'); $('calB').value = q.get('b') || ''; setKind('definite'); }
    if (q.get('at') !== null) $('calLimPoint').value = q.get('at');
    if (q.get('s')) setSide(parseInt(q.get('s'), 10) || 0);
    if (q.get('ta') !== null) $('calTayPoint').value = q.get('ta');
    if (q.get('k') === 'area') { $('calG').value = q.get('g') || '0'; $('calAreaA').value = q.get('aa') || ''; $('calAreaB').value = q.get('ab') || ''; setKind('area'); }
    if (q.get('tn')) $('calTayOrder').value = q.get('tn');
    if (q.get('v')) { const sel = $('calVar'); const o = document.createElement('option'); o.value = q.get('v'); o.textContent = q.get('v'); sel.appendChild(o); sel.value = q.get('v'); }
    return true;
}

function setKind(kind) {
    state.kind = kind;
    setSeg('data-cal-kind', kind);
    $('calBounds').hidden = kind !== 'definite';
    $('calAreaRow').hidden = kind !== 'area';
}
function setSide(side) {
    state.side = side;
    setSeg('data-cal-side', String(side));
}
function setDetail(d, silent) {
    state.detail = d;
    setSeg('data-cal-detail', d);
    if (!silent) try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'detail', d); } catch (_) { /* настройки не мешают работе */ }
}

/* ── выдача ──────────────────────────────────────────────────────────────── */
function solutionText() {
    const lines = [];
    const task = $('calTask').textContent ? textTask() : '';
    if (task) lines.push(task);
    const ans = $('calAnswer');
    const label = (ans.querySelector('.cal-ans-l') || {}).textContent || '';
    const math = answerText();
    if (math) lines.push('', label + t('label_sep') + math);
    const notes = $('calNotes');
    if (!notes.hidden && notes.textContent) lines.push(notes.textContent);
    const check = $('calCheck');
    if (!check.hidden) lines.push(check.textContent);
    if (state.checkText) lines.push(state.checkText);
    if (state.text && state.text.length) lines.push('', lab('pdf_title').trimEnd(), ...state.text);
    if (state.mode === 'derivative' && state.studyText && state.studyText.length && !$('calStudyWrap').hidden) lines.push(...state.studyText);
    return lines.join('\n');
}
function textTask() {
    const q = state.parsed;
    if (!q) return '';
    if (q.special) {
        const o = { locale: LOCALE, raw: true, v: q.special.kind === 'param' ? q.special.t : 'y' };
        return q.special.kind === 'implicit'
            ? listJoin([toText(q.special.L, o) + ' = ' + toText(q.special.R, o), 'y′ = ?'], NT)
            : listJoin(['x = ' + toText(q.special.X, o), 'y = ' + toText(q.special.Y, o), 'y′ₓ = ?'], NT);
    }
    const f = toText(q.node, { raw: true, locale: LOCALE });
    if (state.mode === 'derivative') return '(' + f + ')' + (state.order <= 3 ? ['', '′', '″', '‴'][state.order] : '⁽' + state.order + '⁾') + ' = ?';
    if (state.mode === 'integral' && state.kind === 'area') {
        let g = state.g || '0';
        try { g = toText(parse(state.g || '0').node, { raw: true, locale: LOCALE }); } catch (_) { /* как набрано */ }
        const parts = ['y = ' + f, 'y = ' + g];
        if (state.areaA && state.areaB) parts.push(q.v + ' ∈ ' + ivText(ptText(state.areaA), ptText(state.areaB), NT, 'closed'));
        return areaSymT() + t('label_sep') + listJoin(parts, NT) + '  ' + areaSymT() + ' = ?';
    }
    if (state.mode === 'integral') return (state.kind === 'definite' ? '∫' + ivText(ptText(state.a), ptText(state.b), NT, 'closed') + ' ' : '∫ ') + f + ' d' + q.v + ' = ?';
    if (state.mode === 'taylor') return listJoin(['T' + state.tayOrder + '(' + q.v + ')', q.v + '₀ = ' + ptText(state.tayPoint || '0')], NT) + t('label_sep') + f;
    return 'lim(' + q.v + '→' + ptText(state.limPoint) + (ptIsInf(state.limPoint) ? '' : sideSuffix(state.side, NT)) + ') ' + f + ' = ?';
}
/** Ответ текстом — из дерева, а не из LaTeX: так он вставляется обратно в поле. */
function answerText() { return state.lastText || ''; }

/** Запись в буфер — первым делом в обработчике нажатия: после await Safari её уже не разрешит. */
function copyText(text, okKey, via) {
    if (!text) return;
    settleNow();
    const done = window.__copyText ? window.__copyText(text) : navigator.clipboard.writeText(text);
    Promise.resolve(done).then(() => {
        if (window.__toast) window.__toast(t(okKey), 'success');
        if (via === 'share') funnel('share', { via: 'clipboard', mode: state.mode });
        else { stage('export', { via }); funnel('export', { via, mode: state.mode, len: text.length }); }
    }, () => {
        if (window.__toast) window.__toast(t('copy_failed'), 'error');
        funnel('export_failed', { via, mode: state.mode });
    });
}

function buildReport() {
    const root = document.createElement('div');
    root.className = 'lg-app cal cal-report';
    root.style.cssText = 'padding:0;background:#fff;color:#111827;max-width:none';
    const add = (el) => { const c = el.cloneNode(true); c.removeAttribute('id'); c.querySelectorAll('[id]').forEach((x) => x.removeAttribute('id')); root.appendChild(c); return c; };
    add($('calTask'));
    add($('calAnswer'));
    if (!$('calNotes').hidden) add($('calNotes'));
    if (!$('calCheck').hidden) add($('calCheck'));
    if (!$('calCheckTex').hidden) add($('calCheckTex'));
    if (!$('calStepsWrap').hidden) {
        const h = document.createElement('p');
        h.className = 'cal-side-h';
        h.textContent = t('pdf_title');
        root.appendChild(h);
        add($('calSteps'));
    }
    if (!$('calStudyWrap').hidden) add($('calStudy'));
    const src = $('calPlot');
    if (src.width && src.height && !$('calPlotWrap').hidden) {
        const cv = document.createElement('canvas');
        cv.width = src.width;
        cv.height = src.height;
        cv.style.cssText = 'width:100%;max-width:640px;height:auto;display:block;border:1px solid #d6dde8;border-radius:10px;margin-top:12px';
        try { cv.getContext('2d').drawImage(src, 0, 0); } catch (_) { /* пустой холст лучше упавшего отчёта */ }
        root.appendChild(cv);
    }
    return root;
}

function savePdf() {
    if ($('calCard').hidden) return;
    settleNow();
    const btn = $('calPdf');
    btn.disabled = true;
    const mode = state.mode;
    let name = 'calculus';
    // имя файла — словом страницы («Производная-x^2sinx.pdf»), а не английским названием режима
    const word = state.mode === 'integral' && state.kind === 'area' ? t('int_area') : t('mode_' + state.mode);
    try { if (window.__outputName) name = window.__outputName.join(window.__outputName.sanitize(word + '-' + state.src.replace(/\s+/g, '').slice(0, 36)), 'pdf'); } catch (_) { name = 'calculus.pdf'; }
    // Сборщик PDF — отдельный файл: не доехал при загрузке страницы — добираем по нажатию, а не молчим.
    ensureScript('__generatePDF', 'report-pdf.js').then((ready) => {
        if (!ready) {
            if (window.__toast) window.__toast(t('pdf_error'), 'error');
            critical('asset_failed_report_pdf', { asset: 'report-pdf.js' });
            funnel('export_failed', { via: 'pdf', mode, code: 'script' });
            return null;
        }
        return window.__generatePDF({
            element: buildReport(),
            title: t('pdf_title'),
            filename: /\.pdf$/.test(name) ? name : name + '.pdf',
            strings: { libError: t('pdf_error'), genError: t('pdf_error'), blocked: t('pdf_blocked') },
            pagebreak: { mode: ['css', 'legacy'] },
        });
    }).then((r) => {
        if (!r) return;
        if (r.ok) { stage('export', { via: 'pdf' }); funnel('export', { via: 'pdf', mode, ms: r.ms || 0, kb: r.kb || 0 }); }
        else funnel('export_failed', { via: 'pdf', mode, code: r.code || '' });
    }).catch((e) => caught(e, 'cal_pdf', t('pdf_error'))).then(() => { btn.disabled = false; });
}

function share() {
    saveHash();
    const url = location.href;
    // Системное «Поделиться» — на телефоне; на компьютере ссылку ждут в буфере.
    const phone = !!(navigator.share && window.matchMedia && window.matchMedia('(pointer:coarse)').matches);
    if (!phone) { copyText(url, 'link_copied', 'share'); return; }
    settleNow();
    navigator.share({ title: document.title, text: textTask(), url }).then(() => {
        funnel('share', { via: 'native', mode: state.mode });
    }, (e) => { if (!e || e.name !== 'AbortError') copyText(url, 'link_copied', 'share'); });
}

function plotterUrl() {
    const q = state.parsed;
    if (!q || !CFG.plotterUrl) return '';
    if (q.special) return CFG.plotterUrl;
    const f0 = q.v === 'x' ? q.node : A.subs(q.node, q.v, A.vr('x'));
    const list = [plotterExpr(f0)];
    if (state.mode === 'derivative') {
        const d = diff(f0, 'x');
        if (d) list.push(plotterExpr(simplify(d, 'x')));
    }
    if (list.some((s) => s === null)) return CFG.plotterUrl;
    return CFG.plotterUrl + '#f=' + list.map(encodeURIComponent).join(';');
}

/**
 * Запись для построителя графиков: его разбор (math-core/parse.js) знает
 * asin/atan, log(основание, число), sqrt, abs и ^ — печатаем явно, со скобками.
 * null — функция, которой там нет (обратные гиперболические).
 */
const PLOT_FN = { sin: 'sin', cos: 'cos', tan: 'tan', cot: 'cot', asin: 'asin', acos: 'acos', atan: 'atan', sinh: 'sinh', cosh: 'cosh', tanh: 'tanh', ln: 'ln', abs: 'abs', sign: 'sign' };
function plotterExpr(n) {
    const P = (m) => { const s2 = plotterExpr(m); return s2 === null ? null : '(' + s2 + ')'; };
    const all = (arr) => (arr.some((x) => x === null) ? null : arr);
    switch (n.k) {
        case 'num': return n.r.d === 1n ? n.r.n.toString() : '(' + n.r.n + '/' + n.r.d + ')';
        case 'sym': return n.s === 'pi' ? 'pi' : 'e';
        case 'var': return n.s;
        case 'add': { const p = all(n.t.map(plotterExpr)); return p ? p.join('+').replace(/\+-/g, '-') : null; }
        case 'mul': { const p = all(n.t.map(P)); return p ? p.join('*') : null; }
        case 'div': { const a = P(n.a); const b = P(n.b); return a && b ? a + '/' + b : null; }
        case 'neg': { const a = P(n.a); return a ? '-' + a : null; }
        case 'pow': { const a = P(n.b); const b = P(n.e); return a && b ? a + '^' + b : null; }
        case 'fn': {
            const a = plotterExpr(n.a);
            if (a === null) return null;
            if (n.f === 'sec') return '1/cos(' + a + ')';
            if (n.f === 'csc') return '1/sin(' + a + ')';
            if (n.f === 'coth') return '1/tanh(' + a + ')';
            if (n.f === 'acot') return '(pi/2-atan(' + a + '))';
            const name = PLOT_FN[n.f];
            return name ? name + '(' + a + ')' : null;
        }
        case 'log': { const b = plotterExpr(n.base); const a = plotterExpr(n.a); return b === null || a === null ? null : 'log(' + b + ',' + a + ')'; }
        default: return null;
    }
}
/* ── тренажёр ────────────────────────────────────────────────────────────── */
let practice = null;
function seed() { try { return crypto.getRandomValues(new Uint32Array(1))[0]; } catch (_) { return Math.floor(Math.random() * 4294967295); } }

function newPractice() {
    const sel = $('calTopic');
    const topic = sel.value;
    let task = null;
    for (let i = 0; i < 12 && !task; i++) {
        // примеры пишутся английскими именами и переводятся в запись страны (sen, tg)
        const g0 = fixPoint(generate(topic, seed(), 'tan'));
        const g = Object.assign({}, g0, { src: localizeSrc(g0.src, NT) });
        try {
            const q = readExpr(g.src);
            let ok = false;
            let answer = null;
            if (g.mode === 'derivative') { const r = solveDerivative({ node: q.node, v: q.v, params: [] }); ok = r.ok && r.verified; answer = r.result; }
            else if (g.mode === 'integral') { const r = solveIntegral({ node: q.node, v: q.v, params: [] }); ok = r.indefinite.ok; answer = ok ? r.indefinite.F : null; }
            else { const r = solveLimit({ node: q.node, v: q.v, params: [], point: g.point, side: 0 }); ok = r.limit.kind !== 'unknown'; answer = r.limit; }
            if (ok) task = Object.assign(g, { q, answer });
        } catch (_) { /* следующая попытка */ }
    }
    if (!task) return;
    practice = task;
    const box = $('calTaskP');
    box.innerHTML = '';
    const head = lab('task_' + task.mode);
    box.appendChild(document.createTextNode(head));
    const f = toLatex(task.q.node, { raw: true, locale: LOCALE });
    const latex = task.mode === 'derivative' ? '\\left(' + f + '\\right)\'' : task.mode === 'integral' ? '\\int ' + f + '\\,dx' : '\\lim_{x\\to ' + ptLatex(task.point) + '} ' + f;
    box.appendChild(R.tex(latex, false));
    box.hidden = false;
    $('calPracticeAns').hidden = false;
    $('calPracticeAns').value = '';
    $('calPracticeCheck').hidden = false;
    $('calPracticeShow').hidden = false;
    $('calPracticeMsg').hidden = true;
    funnel('trainer_start', { topic });
}

function checkPractice() {
    if (!practice) return;
    const msg = $('calPracticeMsg');
    const input = $('calPracticeAns');
    const ans = input.value.trim();
    msg.hidden = false;
    if (!ans) { msg.className = 'cal-practice-msg bad'; msg.textContent = t('practice_empty'); return; }
    let ok = false;
    try {
        if (practice.mode === 'limit') ok = checkLimitAnswer(ans, practice.answer);
        else {
            const r = checkAnswer(practice.mode, ans, practice.answer, practice.q.v, {});
            if (r.parse) { msg.className = 'cal-practice-msg bad'; msg.textContent = t('practice_unreadable'); input.setAttribute('aria-invalid', 'true'); return; }
            ok = r.ok;
        }
    } catch (_) {
        msg.className = 'cal-practice-msg bad';
        msg.textContent = t('practice_unreadable');
        input.setAttribute('aria-invalid', 'true');
        return;
    }
    input.removeAttribute('aria-invalid');
    msg.className = 'cal-practice-msg ' + (ok ? 'good' : 'bad');
    msg.textContent = ok ? t('practice_right') : t('practice_wrong');
    funnel('trainer_answer', { ok: ok ? 1 : 0, topic: $('calTopic').value });
}
function checkLimitAnswer(src, L) {
    const s = src.replace(/\s+/g, '').toLowerCase();
    if (/^(\+?∞|\+?inf|\+?infinity|\+?oo)$/.test(s)) return L.kind === '+inf';
    if (/^(-|−)(∞|inf|infinity|oo)$/.test(s)) return L.kind === '-inf';
    if (/^(нет|dne|—|-|не существует|несуществует|doesnotexist)$/.test(s)) return L.kind === 'dne';
    if (L.kind !== 'finite') return false;
    const v = A.evaluate(parse(s).node, {});
    const w = A.evaluate(L.value, {});
    return Number.isFinite(v) && Math.abs(v - w) <= 1e-9 * Math.max(1, Math.abs(w));
}
function showPractice() {
    if (!practice) return;
    setMode(practice.mode);
    $('calInput').value = practice.src;
    if (practice.mode === 'limit') { $('calLimPoint').value = practice.point; setSide(0); }
    if (practice.mode === 'integral') setKind('indefinite');
    state.fromPractice = true;
    touched();
    refresh(true);
    funnel('trainer_show', { topic: $('calTopic').value });
}

/* ── образцы ─────────────────────────────────────────────────────────────── */
function applySample(b, run = true) {
    const mode = b.getAttribute('data-cal-sample-mode');
    setMode(mode);
    $('calInput').value = b.getAttribute('data-cal-sample');
    if (mode === 'integral') {
        if (b.hasAttribute('data-cal-g')) {
            $('calG').value = b.getAttribute('data-cal-g');
            $('calAreaA').value = b.getAttribute('data-cal-a') || '';
            $('calAreaB').value = b.getAttribute('data-cal-b') || '';
            setKind('area');
        } else if (b.hasAttribute('data-cal-a')) { $('calA').value = b.getAttribute('data-cal-a'); $('calB').value = b.getAttribute('data-cal-b'); setKind('definite'); } else setKind('indefinite');
    }
    if (mode === 'limit') { $('calLimPoint').value = b.getAttribute('data-cal-point') || '0'; setSide(parseInt(b.getAttribute('data-cal-side') || '0', 10) || 0); }
    if (mode === 'taylor') $('calTayPoint').value = b.getAttribute('data-cal-point') || '0';
    state.fromPractice = false;
    if (run) { touched(); refresh(true); funnel('example', { mode, from: 'chips' }); }
}

/* ── недавние записи ─────────────────────────────────────────────────────────
 * Домашняя работа — это список примеров: к третьему хочется вернуться к первому. Запись попадает
 * сюда, когда на неё получен ответ и она отстоялась (тот же момент, что «человек получил
 * результат»); хранится на устройстве общим примитивом настроек, наружу не уходит. */
const RECENT_MAX = 8;
const MODES = ['derivative', 'integral', 'limit', 'taylor'];
function recentLoad() {
    let v = [];
    try { if (window.__toolSettings) v = window.__toolSettings.load(SLUG, 'recent', []); } catch (_) { v = []; }
    if (!Array.isArray(v)) return [];
    return v.filter((x) => x && typeof x.h === 'string' && x.h && typeof x.l === 'string' && MODES.indexOf(x.m) >= 0).slice(0, RECENT_MAX);
}
/** Подпись чипа: запись как набрана + то, что отличает задачу (пределы, точка). */
function recentLabel() {
    const src = state.src.replace(/\s+/g, ' ').trim().slice(0, 60);
    if (state.mode === 'integral' && state.kind === 'definite') return '∫ ' + src + ' [' + state.a + NT.iv.sep + state.b + ']';
    if (state.mode === 'integral' && state.kind === 'area') return t('area_sym') + t('label_sep') + src + NT.listSep + (state.g || '0');
    if (state.mode === 'integral') return '∫ ' + src;
    if (state.mode === 'limit') return 'lim ' + src + ', ' + (state.parsed ? state.parsed.v : 'x') + '→' + (state.limPoint || '0');
    if (state.mode === 'taylor') return 'T ' + src + ', ' + (state.parsed ? state.parsed.v : 'x') + '₀ = ' + (state.tayPoint || '0');
    return (state.order > 1 ? 'f⁽' + state.order + '⁾ ' : 'f′ ') + src;
}
function recentAdd() {
    if (!state.src.trim()) return;
    saveHash();
    const h = String(location.hash || '');
    if (!h) return;
    const l = recentLabel();
    const list = recentLoad().filter((x) => x.h !== h && x.l !== l);
    list.unshift({ m: state.mode, l, h });
    try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'recent', list.slice(0, RECENT_MAX)); } catch (_) { /* хранилище закрыто */ }
    renderRecent();
}
function renderRecent() {
    const box = $('calRecent');
    if (!box) return;
    const list = recentLoad();
    box.hidden = !list.length;
    const chips = $('calRecentChips');
    chips.innerHTML = '';
    list.forEach((x, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'tc-chip cal-mono';
        b.dir = 'ltr';
        b.setAttribute('data-cal-recent', String(i));
        b.title = t('mode_' + x.m);
        b.textContent = x.l;
        chips.appendChild(b);
    });
}
/** Открыть недавнюю запись: состояние целиком лежит в её ссылке. */
function openRecent(i) {
    const x = recentLoad()[i];
    if (!x) return;
    try { history.replaceState(null, '', x.h); } catch (_) { location.hash = x.h; }
    if (!loadHash()) return;
    touched();
    state.fromPractice = false;
    refresh(true);
    funnel('recent', { mode: x.m });
}

/* ── запуск ──────────────────────────────────────────────────────────────── */
function init() {
    const input = $('calInput');
    if (!input) return;
    // Разметку рисует сервер записью страницы по умолчанию; у испанской страницы запись зависит
    // от страны читателя: разделитель концов отрезка на чипах и «[a; b]» в подписи — знаком записи.
    document.querySelectorAll('[data-cal-sep]').forEach((el) => { el.textContent = NT.iv.sep; });
    document.querySelectorAll('[data-cal-seg]').forEach((el) => { el.textContent = localizeSeg(el.textContent, NT); });
    let timer = 0;
    const onType = () => { touched(); clearTimeout(timer); timer = setTimeout(() => { state.fromPractice = false; refresh(true); }, 320); };
    const onEnter = (e) => { if (e.key === 'Enter') { touched(); clearTimeout(timer); refresh(true); } };
    const fields = ['calInput', 'calPoint', 'calA', 'calB', 'calG', 'calAreaA', 'calAreaB', 'calLimPoint', 'calTayPoint', 'calTan', 'calSegA', 'calSegB'].map($);
    let lastField = input;
    for (const el of fields) {
        el.addEventListener('input', onType);
        el.addEventListener('keydown', onEnter);
        el.addEventListener('focus', () => { lastField = el; });
    }
    for (const id of ['calOrder', 'calVar', 'calTayOrder']) $(id).addEventListener('change', () => { touched(); refresh(true); });

    // Та же функция имеет смысл во всех трёх режимах: переключение её не стирает.
    document.querySelectorAll('[data-cal-mode]').forEach((b) => b.addEventListener('click', () => { setMode(b.getAttribute('data-cal-mode'), { fill: !input.value.trim() }); refresh(true); }));
    document.querySelectorAll('[data-cal-kind]').forEach((b) => b.addEventListener('click', () => { const k = b.getAttribute('data-cal-kind'); if (k !== state.kind) funnel('kind', { kind: k }); setKind(k); refresh(true); }));
    document.querySelectorAll('[data-cal-side]').forEach((b) => {
        if (!b.classList.contains('tc-seg-b')) return;
        b.addEventListener('click', () => { setSide(parseInt(b.getAttribute('data-cal-side'), 10) || 0); refresh(true); });
    });
    document.querySelectorAll('[data-cal-detail]').forEach((b) => b.addEventListener('click', () => {
        setDetail(b.getAttribute('data-cal-detail'));
        const apply = () => refresh(false);
        if (window.__viewTransition) window.__viewTransition(apply); else apply();
        funnel('setting', { k: 'detail', v: state.detail });
    }));
    // Вставка знака — в место курсора того поля, где он стоял. В телеметрию — один раз за страницу:
    // «панелью пользуются», а не событие на каждое нажатие.
    let keysUsed = false;
    document.querySelectorAll('[data-cal-key]').forEach((b) => b.addEventListener('click', (e) => {
        e.preventDefault();
        const field = lastField || input;
        const ins = b.getAttribute('data-cal-key');
        const back = parseInt(b.getAttribute('data-cal-back') || '0', 10) || 0;
        const a = field.selectionStart == null ? field.value.length : field.selectionStart;
        const z = field.selectionEnd == null ? a : field.selectionEnd;
        field.value = field.value.slice(0, a) + ins + field.value.slice(z);
        const pos = a + ins.length - back;
        try { field.setSelectionRange(pos, pos); } catch (_) { /* поле без выделения */ }
        // На телефоне фокус в поле поднимает системную клавиатуру, и она закрывает ту самую панель,
        // по которой человек набирает: фокус возвращаем, только если он в поле уже был (или это мышь).
        if (document.activeElement === field || !coarsePointer()) field.focus();
        onType();
        if (!keysUsed) { keysUsed = true; funnel('keys', { k: ins.slice(0, 6) }); }
    }));
    // нажатие клавиши панели не забирает фокус у поля: каретка и системная клавиатура остаются как были
    document.querySelector('.cal-keys').addEventListener('pointerdown', (e) => { if (e.target.closest('[data-cal-key]')) e.preventDefault(); });
    document.querySelectorAll('[data-cal-sample]').forEach((b) => b.addEventListener('click', () => applySample(b)));

    $('calPdf').addEventListener('click', savePdf);
    $('calCopy').addEventListener('click', () => copyText(solutionText(), 'copied', 'copy'));
    $('calCopyTex').addEventListener('click', () => copyText((state.latex || []).join('\n'), 'latex_copied', 'latex'));
    $('calShare').addEventListener('click', share);
    $('calOpenPlot').addEventListener('click', () => { const url = plotterUrl(); if (!url) return; settleNow(); funnel('export', { via: 'plotter', mode: state.mode }); window.location.href = url; });
    $('calRecent').addEventListener('click', (e) => {
        const rec = e.target.closest('[data-cal-recent]');
        if (rec) { openRecent(+rec.getAttribute('data-cal-recent')); return; }
        if (e.target.closest('#calRecentClear')) { try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'recent', []); } catch (_) { /* хранилище закрыто */ } renderRecent(); funnel('recent_clear', {}); }
    });
    $('calKatexRetry').addEventListener('click', () => loadKatex(true));

    $('calPracticeNew').addEventListener('click', newPractice);
    $('calPracticeCheck').addEventListener('click', checkPractice);
    $('calPracticeAns').addEventListener('keydown', (e) => { if (e.key === 'Enter') checkPractice(); });
    $('calPracticeShow').addEventListener('click', showPractice);
    $('calDnot').addEventListener('change', () => { state.dnot = $('calDnot').value; refresh(false); funnel('setting', { k: 'dnot', v: state.dnot }); });
    let studyUsed = false;
    for (const id of ['calTan', 'calSegA', 'calSegB']) $(id).addEventListener('input', () => { if (!studyUsed) { studyUsed = true; funnel('study', { field: id === 'calTan' ? 'tangent' : 'segment' }); } });
    // Шпаргалка записи в пустом состоянии: строка — готовый пример.
    document.querySelectorAll('[data-cal-cheat]').forEach((b) => b.addEventListener('click', () => {
        setMode(b.getAttribute('data-cal-cheat-mode'));
        if (state.mode === 'integral') setKind('indefinite');
        input.value = b.getAttribute('data-cal-cheat');
        state.fromPractice = false;
        touched();
        refresh(true);
        funnel('example', { mode: state.mode, from: 'cheat' });
    }));

    // настройки человека (запись производной, подробность) — на DOMContentLoaded
    try {
        if (window.__toolSettings) {
            window.__toolSettings.bind(SLUG, $('calDnot'), 'dnot', 'prime');
            state.dnot = $('calDnot').value || 'prime';
            const d = window.__toolSettings.load(SLUG, 'detail', 'short');
            if (d === 'full') setDetail('full', true);
        }
    } catch (_) { /* настройки не мешают работе */ }

    // тема практики по текущему режиму
    const syncTopic = () => {
        const sel = $('calTopic');
        const cur = sel.options[sel.selectedIndex];
        if (cur && cur.getAttribute('data-mode') !== state.mode) {
            const first = Array.from(sel.options).find((o) => o.getAttribute('data-mode') === state.mode);
            if (first) sel.value = first.value;
        }
    };
    document.querySelectorAll('[data-cal-mode]').forEach((b) => b.addEventListener('click', syncTopic));

    if (!loadHash()) {
        setMode('derivative', { silent: true });
        input.value = 'x^2 sin x';
    }
    syncTopic();
    renderRecent();
    whenKatex(() => { renderCheat(); refresh(false); });

    let raf = 0;
    window.addEventListener('resize', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => { if (lastPlot) renderPlot(lastPlot); }); });
    const mo = new MutationObserver(() => { if (lastPlot) renderPlot(lastPlot); });
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

    // Печать: формула шире листа (вторая производная дроби — 1000 px одной строкой, переносить
    // её негде) обрезалась краем бумаги. На время печати такие формулы ужимаются до ширины своей
    // колонки на листе, после — возвращаются.
    const fitPrint = () => {
        let printing = false;
        try { printing = window.matchMedia('print').matches; } catch (_) { printing = false; }
        document.querySelectorAll('#calApp .cal-tex').forEach((el) => {
            el.style.zoom = '';
            const k = el.querySelector('.katex-html, math');
            if (!k) return;
            const w = k.getBoundingClientRect().width;
            // до печати раскладка ещё экранная — меряем по ширине листа A4/Letter (680 px), на печати — по колонке
            const room = printing ? Math.min(680, el.parentElement.getBoundingClientRect().width || 680) : 680;
            if (w > room + 1) el.style.zoom = String(Math.max(0.35, room / w));
        });
    };
    const unfitPrint = () => { document.querySelectorAll('#calApp .cal-tex').forEach((el) => { el.style.zoom = ''; }); };
    window.addEventListener('beforeprint', fitPrint);
    window.addEventListener('afterprint', unfitPrint);
    try { window.matchMedia('print').addEventListener('change', (e) => { if (e.matches) fitPrint(); else unfitPrint(); }); } catch (_) { /* старый Safari: только beforeprint */ }

    // Человек вставил другую ссылку в ту же вкладку или вернулся «назад» к прежней задаче:
    // свои правки адреса (replaceState) этого события не дают.
    window.addEventListener('hashchange', () => { if (loadHash()) { state.auto = true; state.fromPractice = false; refresh(false); } });

    // Опора для стендов: состояние видно снаружи без чтения пикселей.
    window.__calState = state;
}

/** Формулы шпаргалки — один раз, когда KaTeX на месте. */
function renderCheat() {
    document.querySelectorAll('[data-cal-tex]').forEach((el) => {
        el.innerHTML = '';
        el.appendChild(R.tex(el.getAttribute('data-cal-tex'), false));
    });
}

const coarsePointer = () => { try { return !!(window.matchMedia && window.matchMedia('(pointer:coarse)').matches); } catch (_) { return false; } };

/**
 * KaTeX грузится классическим defer-скриптом; если опоздал — перерисуем. Если не приехал вовсе
 * (общая лестница запасных адресов отработала, а скрипта нет) — формулы остаются текстовой
 * записью, и раньше человек видел «\\dfrac{…}» без единого слова. Теперь — строка с объяснением
 * и «Повторить»; сбой уходит критическим сигналом.
 */
let redraw = null;
function whenKatex(fn) {
    redraw = fn;
    if (window.katex) { fn(); cssLate(); return; }
    fn();
    let n = 0;
    const iv = setInterval(() => {
        if (window.katex) { clearInterval(iv); $('calKatexFail').hidden = true; fn(); cssLate(); return; }
        if (++n > 60) { clearInterval(iv); loadKatex(false); }
    }, 100);
}
/** Стили KaTeX пришли с запасного адреса позже скрипта: формулы нарисованы одним MathML —
 *  как только стили на месте, перерисовываем полноценной вёрсткой. */
function cssLate() {
    if (katexCss()) return;
    let n = 0;
    const iv = setInterval(() => {
        if (katexCss()) { clearInterval(iv); if (redraw) redraw(); return; }
        if (++n > 40) clearInterval(iv);
    }, 750);
}
let katexLoading = false;
function loadKatex(byHand) {
    if (katexLoading) return;
    katexLoading = true;
    if (byHand) funnel('retry', { what: 'katex' });
    ensureScript('katex', 'katex.min.js', '/vendor/katex/katex.min.js').then((ready) => {
        katexLoading = false;
        $('calKatexFail').hidden = !!ready;
        if (ready) { if (redraw) redraw(); cssLate(); return; }
        critical('asset_failed_katex', { asset: 'katex.min.js', by_hand: byHand ? 1 : 0 });
    });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

export { simplify };
